import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import enStrings from "../../../i18n/locales/en.json";
import arStrings from "../../../i18n/locales/ar.json";
import StatCard from "../../../components/Kids/StatCard";
import Skeleton from "../../../components/Kids/Skeleton";

const API   = process.env.REACT_APP_API_URL;
const token = () => JSON.parse(localStorage.getItem("edo-admin"))?.token;

const SUBJECTS   = ["math", "english", "science", "arabic", "art", "coding", "general"];
const GAME_TYPES = ["quiz", "puzzle", "drag_drop", "typing", "drawing", "story", "flashcard", "tracing", "memory", "connect_lines"];
const DIFFS      = ["easy", "medium", "hard"];
const PLANS      = ["starter", "pro", "premium"];

export default function KidsAdmin() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("overview");
  const auth = { headers: { Authorization: `Bearer ${token()}` } };

  return (
    <div className="max-w-6xl mx-auto bg-[#F7F8FA] rounded-2xl p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-5">
        <span className="text-2xl">🌟</span>
        <h1 className="font-display font-semibold text-2xl text-navy">{t("admin.title")}</h1>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
        {[["overview", t("admin.tabs.overview")], ["students", t("admin.tabs.students")], ["schools", "🏫 Schools"], ["games", t("admin.tabs.games")], ["taxonomy", "🏷️ Categories"], ["promo", "🎁 Promotions"], ["reports", "📈 Reports"], ["plans", t("admin.tabs.plans")], ["requests", t("admin.tabs.requests")], ["leads", "🧪 Trial Leads"], ["map", t("admin.tabs.map")], ["email", t("admin.tabs.email")], ["blog", "📝 Blog"], ["lang", "🌐 Language"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-extrabold border-b-2 -mb-px transition whitespace-nowrap ${
              tab === k ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview auth={auth} />}
      {tab === "students" && <Students auth={auth} />}
      {tab === "schools"  && <Schools auth={auth} />}
      {tab === "games"    && <Games auth={auth} />}
      {tab === "taxonomy" && <Taxonomy auth={auth} />}
      {tab === "promo"    && <PromoCampaigns auth={auth} />}
      {tab === "reports"  && <PlatformReports auth={auth} />}
      {tab === "plans"    && <Plans auth={auth} />}
      {tab === "requests" && <Requests auth={auth} />}
      {tab === "leads"    && <TrialLeads auth={auth} />}
      {tab === "map"      && <MapThemes auth={auth} />}
      {tab === "email"    && <EmailBroadcast auth={auth} />}
      {tab === "blog"     && <BlogAdmin auth={auth} />}
      {tab === "lang"     && <LanguageEditor />}
    </div>
  );
}

/* ───────────── OVERVIEW ───────────── */
function Overview({ auth }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/edolutions/admin/kids/overview`, auth)
      .then(r => setStats(r.data.data)).catch(() => toast.error("Failed to load overview."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) return <SkStatGrid count={12} cols="sm:grid-cols-2 lg:grid-cols-4" />;
  const cards = [
    ["👪", t("admin.overview.parents"), stats.parents], ["🧒", t("admin.overview.children"), stats.children],
    ["🎮", t("admin.overview.games"), stats.games], ["✅", t("admin.overview.activeGames"), stats.active_games],
    ["⏳", t("admin.overview.trials"), stats.trialing], ["💳", t("admin.overview.activeSubs"), stats.active_subs],
    ["🚫", t("admin.overview.cancelled"), stats.cancelled_subs], ["🏆", t("admin.overview.gamesPlayed"), stats.games_played],
    ["📩", t("admin.overview.pendingRequests"), stats.pending_requests ?? 0],
    ["📅", "Today's signups", stats.signups_today ?? 0],
    ["📆", "This week", stats.signups_week ?? 0],
    ["📊", "This month", stats.signups_month ?? 0],
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(([i, l, v]) => (
        <StatCard key={l} emoji={i} value={v} label={l} />
      ))}
    </div>
  );
}

/* ───────────── STUDENTS ───────────── */
function Students({ auth }) {
  const { t } = useTranslation();
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/kids/parents${q ? `?search=${encodeURIComponent(q)}` : ""}`, auth);
      setParents(r.data.data || []);
    } catch { toast.error("Failed to load students."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleParent = async (parentId) => {
    try {
      const r = await axios.put(`${API}/api/edolutions/admin/kids/parents/${parentId}/toggle`, {}, auth);
      toast.success(r.data.message);
      load(search);
    } catch { toast.error("Couldn't update customer status."); }
  };

  const toggleChild = async (childId) => {
    try {
      const r = await axios.put(`${API}/api/edolutions/admin/kids/children/${childId}/toggle`, {}, auth);
      toast.success(r.data.message);
      load(search);
    } catch { toast.error("Couldn't update child."); }
  };

  const deleteChild = async (child) => {
    if (!window.confirm(`Permanently delete "${child.name}"? This removes their profile, progress, XP, and rewards forever — it cannot be undone.`)) return;
    if (window.prompt(`Type the child's name (${child.name}) to confirm deletion:`) !== child.name) {
      return toast.warn("Name didn't match — deletion cancelled.");
    }
    try {
      const r = await axios.delete(`${API}/api/edolutions/admin/kids/children/${child.id}`, auth);
      toast.success(r.data.message);
      load(search);
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete student."); }
  };

  const deleteParent = async (parent) => {
    const email = parent.email || parent.user?.email || "";
    if (!window.confirm(
      `Permanently delete the account for "${parent.name}" (${email})? This deletes their login, ALL of their children's profiles, progress, and subscription — everything. This cannot be undone.`
    )) return;
    if (window.prompt(`Type the parent's email (${email}) to confirm deletion:`) !== email) {
      return toast.warn("Email didn't match — deletion cancelled.");
    }
    try {
      const r = await axios.delete(`${API}/api/edolutions/admin/kids/parents/${parent.id}`, auth);
      toast.success(r.data.message);
      load(search);
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete account."); }
  };

  const resetPin = async (childId) => {
    const pin = window.prompt("Enter a new 4–6 digit PIN (leave blank to clear the PIN):", "");
    if (pin === null) return;
    if (pin && !/^\d{4,6}$/.test(pin)) return toast.warn("PIN must be 4–6 digits.");
    try {
      const r = await axios.post(`${API}/api/edolutions/admin/kids/children/${childId}/reset-pin`, { pin: pin || null }, auth);
      toast.success(r.data.message);
      load(search);
    } catch { toast.error("Couldn't update PIN."); }
  };

  const editGrade = async (childId, current) => {
    const val = window.prompt("Grade (0 = KG, 1–6):", String(current ?? 0));
    if (val === null) return;
    const grade = parseInt(val, 10);
    if (Number.isNaN(grade) || grade < 0 || grade > 6) return toast.warn("Enter a number from 0 (KG) to 6.");
    try {
      const r = await axios.put(`${API}/api/edolutions/admin/kids/children/${childId}`, { grade }, auth);
      toast.success(r.data.message);
      load(search);
    } catch { toast.error("Couldn't update grade."); }
  };

  const setAiLimit = async (parentId, current) => {
    const val = window.prompt("Monthly AI message limit for this family (0 = unlimited):", String(current ?? 0));
    if (val === null) return;
    const limit = parseInt(val, 10);
    if (Number.isNaN(limit) || limit < 0) return toast.warn("Enter a number (0 or more).");
    try {
      const r = await axios.post(`${API}/api/edolutions/admin/kids/parents/${parentId}/ai-limit`, { limit }, auth);
      toast.success(r.data.message);
      load(search);
    } catch { toast.error("Couldn't update AI limit."); }
  };

  if (loading) return <SkParentCards count={4} />;

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && load(search)}
          placeholder={t("admin.students.searchPlaceholder")}
          className="flex-1 max-w-md px-4 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand" />
        <button onClick={() => load(search)} className="bg-navy text-white px-4 py-2 rounded-xl text-sm font-semibold">{t("admin.students.search")}</button>
      </div>

      {parents.length === 0 ? <Empty text={t("admin.students.noParents")} /> : parents.map(p => (
        <div key={p.id} className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div className="font-bold text-navy">{p.name}</div>
              <div className="text-sm text-gray-500">{p.email} · {p.phone || "no phone"}{p.school ? ` · 🏫 ${p.school}` : ""}</div>
              <div className="text-xs text-gray-400 mt-0.5">
                Joined {p.signed_up || "—"} · Last login: {p.last_login || "—"}
                {p.email_verified === false && <span className="ms-1 text-red-500 font-medium">· ✉️ Unverified</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggleParent(p.id)}
                className={`text-xs font-semibold px-2 py-1 rounded-full cursor-pointer ${p.status === "Active" ? "bg-[#DFF5E6] text-[#22874E] hover:bg-green-200" : "bg-red-100 text-red-600 hover:bg-red-200"}`}>
                {p.status === "Active" ? "✓ Active" : "✗ Inactive"}
              </button>
              {p.subscription && (
                <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-brand">
                  {p.subscription.plan} · {p.subscription.status}
                  {p.subscription.status === "trialing" ? ` (${p.subscription.days_left}d)` : ""}
                </span>
              )}
              <span className="text-xs px-2 py-1 rounded-full bg-violet-100 text-violet-700">
                🤖 AI {p.ai_used ?? 0}{p.ai_limit > 0 ? `/${p.ai_limit}` : " · ∞"}
              </span>
              <button onClick={() => setAiLimit(p.id, p.ai_limit)}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100">
                {t("admin.students.setAiLimit")}
              </button>
              {p.whatsapp_url && (
                <a href={p.whatsapp_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs font-semibold px-2 py-1 rounded-lg bg-green-50 text-green-700 hover:bg-green-100">
                  💬 WhatsApp
                </a>
              )}
              <button onClick={() => deleteParent(p)}
                title="Permanently delete this parent's account and login — not just their children"
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">
                🗑️ Delete Account
              </button>
            </div>
          </div>

          {p.children.length === 0 ? (
            <p className="text-sm text-gray-400">{t("admin.students.noChildren")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {p.children.map(c => (
                <div key={c.id} className={`rounded-xl border p-3 ${c.is_active ? "border-gray-200" : "border-red-200 bg-red-50/40 opacity-70"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-gray-800 text-sm">{c.name}</div>
                    <button onClick={() => editGrade(c.id, c.grade)}
                      className="text-xs text-gray-400 hover:text-brand hover:underline" title="Edit grade">
                      {c.grade_label} ✏️
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Lv {c.level} · {c.total_xp} XP · 🔥{c.streak_days} · 🎮{c.games_completed ?? 0} games · ⏱{c.total_time_mins ?? 0}m</div>
                  <div className="text-xs text-gray-400 mt-0.5">{c.has_pin ? t("admin.students.pinSet") : t("admin.students.noPin")}</div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => toggleChild(c.id)}
                      className={`text-xs font-semibold px-2 py-1 rounded-lg ${c.is_active ? "bg-red-50 text-red-500 hover:bg-red-100" : "bg-green-50 text-green-600 hover:bg-green-100"}`}>
                      {c.is_active ? t("admin.students.disable") : t("admin.students.enable")}
                    </button>
                    <button onClick={() => resetPin(c.id)}
                      className="text-xs font-semibold px-2 py-1 rounded-lg bg-orange-50 text-brand hover:bg-orange-100">
                      {t("admin.students.resetPin")}
                    </button>
                    <button onClick={() => deleteChild(c)}
                      className="text-xs font-semibold px-2 py-1 rounded-lg bg-red-100 text-red-700 hover:bg-red-200" title="Permanently delete this student">
                      🗑️ {t("admin.students.delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ───────────── SCHOOLS ───────────── */
const SCHOOL_PLANS = ["basic", "standard", "premium"];
const BLANK_SCHOOL = {
  name: "", email: "", phone: "", address: "", city: "", country: "",
  max_students: 200, plan: "basic",
  admin_name: "", admin_email: "", admin_password: "",
};

function Schools({ auth }) {
  const [schools, setSchools]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null);
  const [report, setReport]     = useState(null);
  const [reportData, setReportData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/schools`, auth);
      setSchools(r.data.data || []);
    } catch { toast.error("Failed to load schools."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    try {
      const r = await axios.put(`${API}/api/edolutions/admin/schools/${s.id}/toggle`, {}, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't toggle school."); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This will also delete the admin account. Schools with students cannot be deleted.`)) return;
    try {
      const r = await axios.delete(`${API}/api/edolutions/admin/schools/${s.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete school."); }
  };

  const viewReport = async (s) => {
    setReport(s.id);
    setReportData(null);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/schools/${s.id}/report`, auth);
      setReportData(r.data.data);
    } catch { toast.error("Failed to load report."); setReport(null); }
  };

  if (loading) return <SkTable cols={6} rows={5} />;

  if (report && reportData) return (
    <SchoolReport data={reportData} onBack={() => { setReport(null); setReportData(null); }} />
  );
  if (report) return (
    <div>
      <Skeleton className="h-4 w-32 mb-4" rounded="6px" />
      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 mb-5 space-y-2">
        <Skeleton className="h-5 w-48" rounded="6px" />
        <Skeleton className="h-3 w-72" rounded="4px" />
      </div>
      <div className="mb-5">
        <SkStatGrid count={5} cols="sm:grid-cols-2 lg:grid-cols-5" />
      </div>
      <SkTable cols={9} rows={5} />
    </div>
  );

  return (
    <div>
      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4 mb-5">
        <p className="font-display font-semibold text-navy mb-1">🏫 School Management</p>
        <p className="text-sm text-gray-600">
          Create and manage schools. Each school gets its own admin account (role 6) who can manage their students independently.
        </p>
      </div>

      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...BLANK_SCHOOL })}
          className="bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2 rounded-xl text-sm">+ New School</button>
      </div>

      {schools.length === 0 ? <Empty text="No schools yet." /> : (
        <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA] text-gray-500 text-start">
              <tr>
                <th className="px-4 py-3">School</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Students</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {schools.map(s => (
                <tr key={s.id} className="border-t border-[#EEF1F5] align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{s.name}</div>
                    <div className="text-xs text-gray-400">{s.email || "—"} · {s.phone || "—"}</div>
                    {(s.city || s.country) && <div className="text-xs text-gray-400">{[s.city, s.country].filter(Boolean).join(", ")}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-700">{s.admin?.name || "—"}</div>
                    <div className="text-xs text-gray-400">{s.admin?.email || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-navy">{s.children_count ?? 0}</span>
                    <span className="text-xs text-gray-400"> / {s.max_students}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-700 capitalize">{s.plan}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggle(s)}
                      className={`text-xs font-semibold px-2 py-1 rounded-full cursor-pointer ${s.is_active ? "bg-[#DFF5E6] text-[#22874E] hover:bg-green-200" : "bg-red-100 text-red-600 hover:bg-red-200"}`}>
                      {s.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    <button onClick={() => viewReport(s)} className="text-blue-600 hover:underline me-3">Report</button>
                    <button onClick={() => setEditing(s)} className="text-navy hover:underline me-3">Edit</button>
                    <button onClick={() => remove(s)} className="text-red-500 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <SchoolModal school={editing} auth={auth} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function SchoolReport({ data, onBack }) {
  const cards = [
    ["🧒", "Students", data.total_students],
    ["👪", "Parents", data.total_parents],
    ["🎮", "Games Played", data.games_played],
    ["⭐", "Total XP", data.total_xp],
    ["🔥", "Active Streaks", data.active_streaks],
  ];

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-navy mb-4 flex items-center gap-1">
        ← Back to schools
      </button>

      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 mb-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-semibold text-xl text-navy">📊 {data.school?.name}</h2>
            <div className="text-sm text-gray-500 mt-1">
              Admin: {data.school?.admin?.name} ({data.school?.admin?.email})
              <span className="ms-3 capitalize">Plan: <b>{data.school?.plan}</b></span>
              <span className="ms-3">Max: <b>{data.school?.max_students}</b></span>
            </div>
          </div>
          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${data.school?.is_active ? "bg-[#DFF5E6] text-[#22874E]" : "bg-red-100 text-red-600"}`}>
            {data.school?.is_active ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
        {cards.map(([icon, label, value]) => (
          <div key={label} className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-4">
            <span className="text-xl block mb-1">{icon}</span>
            <div className="text-xl font-extrabold text-navy">{value ?? 0}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      <h3 className="font-display font-semibold text-navy mb-3">Students</h3>
      {(!data.students || data.students.length === 0) ? <Empty text="No students in this school." /> : (
        <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA] text-gray-500 text-start">
              <tr>
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Grade</th>
                <th className="px-4 py-3">Parent</th><th className="px-4 py-3">Level</th>
                <th className="px-4 py-3">XP</th><th className="px-4 py-3">Streak</th>
                <th className="px-4 py-3">Games</th><th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.students.map(s => (
                <tr key={s.id} className="border-t border-[#EEF1F5]">
                  <td className="px-4 py-3 font-semibold text-gray-800">{s.name}</td>
                  <td className="px-4 py-3">{s.grade_label}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{s.parent_name || "—"}</div>
                    <div className="text-xs text-gray-400">{s.parent_email || ""}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold">{s.level}</td>
                  <td className="px-4 py-3">{s.total_xp}</td>
                  <td className="px-4 py-3">🔥 {s.streak_days}</td>
                  <td className="px-4 py-3">{s.games_completed}</td>
                  <td className="px-4 py-3 text-xs">{s.time_mins}m</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.is_active ? "bg-[#DFF5E6] text-[#22874E]" : "bg-red-100 text-red-600"}`}>
                      {s.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SchoolModal({ school, auth, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK_SCHOOL, ...school });
  const [saving, setSaving] = useState(false);
  const isNew = !school.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.warn("School name is required.");
    if (isNew) {
      if (!form.admin_email.trim()) return toast.warn("Admin email is required.");
      if (!form.admin_name.trim()) return toast.warn("Admin name is required.");
      if (!form.admin_password || form.admin_password.length < 8) return toast.warn("Admin password must be at least 8 characters.");
    }
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API}/api/edolutions/admin/schools`, form, auth);
        toast.success("School created with admin account.");
      } else {
        const { admin_name, admin_email, admin_password, ...payload } = form;
        await axios.put(`${API}/api/edolutions/admin/schools/${school.id}`, payload, auth);
        toast.success("School updated.");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save school.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-semibold text-lg text-navy mb-4">{isNew ? "New School" : `Edit: ${school.name}`}</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <L label="School Name"><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
          <L label="Email"><input type="email" className={inp} value={form.email || ""} onChange={e => set("email", e.target.value)} /></L>
          <L label="Phone"><input className={inp} value={form.phone || ""} onChange={e => set("phone", e.target.value)} /></L>
          <L label="City"><input className={inp} value={form.city || ""} onChange={e => set("city", e.target.value)} /></L>
          <L label="Country"><input className={inp} value={form.country || ""} onChange={e => set("country", e.target.value)} /></L>
          <L label="Max Students"><input type="number" className={inp} value={form.max_students} onChange={e => set("max_students", +e.target.value)} /></L>
          <L label="Plan"><Sel value={form.plan || "basic"} opts={SCHOOL_PLANS} onChange={v => set("plan", v)} /></L>
        </div>

        <div className="mt-4">
          <L label="Address"><input className={inp} value={form.address || ""} onChange={e => set("address", e.target.value)} /></L>
        </div>

        {isNew && (
          <div className="mt-5 rounded-xl bg-orange-50 border border-orange-100 p-4">
            <p className="font-display font-semibold text-sm text-navy mb-3">👤 School Admin Account</p>
            <p className="text-xs text-gray-500 mb-3">This creates a user with role 6 who can log in and manage this school's students.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <L label="Admin Name"><input className={inp} value={form.admin_name} onChange={e => set("admin_name", e.target.value)} /></L>
              <L label="Admin Email"><input type="email" className={inp} value={form.admin_email} onChange={e => set("admin_email", e.target.value)} /></L>
              <L label="Password (min 8)"><input type="password" className={inp} value={form.admin_password} onChange={e => set("admin_password", e.target.value)} /></L>
            </div>
          </div>
        )}

        {!isNew && school.admin && (
          <div className="mt-4 rounded-xl bg-[#F7F8FA] border border-[#EEF1F5] p-3">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Admin:</span> {school.admin.name} ({school.admin.email})
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl text-sm font-semibold bg-brand hover:bg-brand-hover disabled:opacity-50 text-white">
            {saving ? "Saving…" : isNew ? "Create School" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────── TAXONOMY: Subjects (full CRUD) + Game Types (label/icon/enabled/order only) ───────────── */
function Taxonomy({ auth }) {
  const [subjects, setSubjects]   = useState([]);
  const [types, setTypes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [newSubject, setNewSubject] = useState({ key: "", label: "", emoji: "", color: "#6A47E0" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        axios.get(`${API}/api/edolutions/admin/kids/subjects`, auth),
        axios.get(`${API}/api/edolutions/admin/kids/game-types`, auth),
      ]);
      setSubjects(s.data.data || []);
      setTypes(g.data.data || []);
    } catch { toast.error("Failed to load categories."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSubject = async () => {
    if (!newSubject.key.trim() || !newSubject.label.trim()) return toast.warn("Key and label are required.");
    if (!/^[a-z0-9_-]+$/i.test(newSubject.key)) return toast.warn("Key may only contain letters, numbers, - and _.");
    try {
      await axios.post(`${API}/api/edolutions/admin/kids/subjects`, newSubject, auth);
      toast.success("Subject added.");
      setNewSubject({ key: "", label: "", emoji: "", color: "#6A47E0" });
      load();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't add subject."); }
  };

  const saveSubject = async (s, patch) => {
    try {
      await axios.put(`${API}/api/edolutions/admin/kids/subjects/${s.id}`, patch, auth);
      setSubjects(list => list.map(x => x.id === s.id ? { ...x, ...patch } : x));
    } catch { toast.error("Couldn't update subject."); }
  };

  const toggleSubject = async (s) => {
    try {
      const r = await axios.put(`${API}/api/edolutions/admin/kids/subjects/${s.id}/toggle`, {}, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't update subject."); }
  };

  const saveType = async (gt, patch) => {
    try {
      await axios.put(`${API}/api/edolutions/admin/kids/game-types/${gt.id}`, patch, auth);
      setTypes(list => list.map(x => x.id === gt.id ? { ...x, ...patch } : x));
    } catch { toast.error("Couldn't update game type."); }
  };

  if (loading) return <SkTable cols={4} rows={6} />;

  return (
    <div className="space-y-8">
      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4">
        <p className="font-display font-semibold text-navy mb-1">🏷️ Subjects &amp; Game Types</p>
        <p className="text-sm text-gray-600">
          <b>Subjects</b> are fully yours — add as many as you like, kids see them immediately as filter chips.
          <b> Game Types</b> are the fixed set of playable engines already built into the app — you can rename,
          re-icon, reorder, or hide them, but can't create a brand-new one here (that needs new code).
        </p>
      </div>

      {/* ── Subjects ── */}
      <div>
        <h3 className="font-display font-semibold text-navy mb-3">Subjects</h3>
        <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA] text-gray-500 text-start">
              <tr>
                <th className="px-4 py-3">Key</th><th className="px-4 py-3">Emoji</th>
                <th className="px-4 py-3">Label</th><th className="px-4 py-3">Color</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {subjects.map(s => (
                <tr key={s.id} className="border-t border-[#EEF1F5]">
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{s.key}</td>
                  <td className="px-4 py-2">
                    <input defaultValue={s.emoji || ""} onBlur={e => e.target.value !== s.emoji && saveSubject(s, { emoji: e.target.value })}
                      className="w-14 px-2 py-1 rounded-lg border border-gray-200 text-center outline-none focus:border-brand" />
                  </td>
                  <td className="px-4 py-2">
                    <input defaultValue={s.label} onBlur={e => e.target.value !== s.label && saveSubject(s, { label: e.target.value })}
                      className="w-full max-w-[160px] px-2 py-1 rounded-lg border border-gray-200 outline-none focus:border-brand" />
                  </td>
                  <td className="px-4 py-2">
                    <input type="color" defaultValue={s.color || "#6A47E0"} onChange={e => saveSubject(s, { color: e.target.value })}
                      className="w-10 h-8 rounded cursor-pointer" />
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={() => toggleSubject(s)}
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${s.is_active ? "bg-[#DFF5E6] text-[#22874E] hover:bg-green-200" : "bg-red-100 text-red-600 hover:bg-red-200"}`}>
                      {s.is_active ? "Active" : "Disabled"}
                    </button>
                  </td>
                  <td className="px-4 py-2"></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-4">
          <p className="text-xs font-semibold text-gray-500 mb-2">+ Add a subject</p>
          <div className="flex flex-wrap gap-2 items-center">
            <input placeholder="key (e.g. music)" value={newSubject.key}
              onChange={e => setNewSubject(f => ({ ...f, key: e.target.value.toLowerCase() }))}
              className="w-32 px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-brand" />
            <input placeholder="emoji" value={newSubject.emoji}
              onChange={e => setNewSubject(f => ({ ...f, emoji: e.target.value }))}
              className="w-16 px-2 py-1.5 rounded-lg border border-gray-200 text-sm text-center outline-none focus:border-brand" />
            <input placeholder="Label" value={newSubject.label}
              onChange={e => setNewSubject(f => ({ ...f, label: e.target.value }))}
              className="w-40 px-2 py-1.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-brand" />
            <input type="color" value={newSubject.color}
              onChange={e => setNewSubject(f => ({ ...f, color: e.target.value }))}
              className="w-10 h-8 rounded cursor-pointer" />
            <button onClick={addSubject} className="bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-1.5 rounded-xl text-sm">
              Add
            </button>
          </div>
        </div>
      </div>

      {/* ── Game Types ── */}
      <div>
        <h3 className="font-display font-semibold text-navy mb-3">Game Types</h3>
        <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA] text-gray-500 text-start">
              <tr>
                <th className="px-4 py-3">Key</th><th className="px-4 py-3">Icon</th>
                <th className="px-4 py-3">Label</th><th className="px-4 py-3">Order</th><th className="px-4 py-3">Shown to kids</th>
              </tr>
            </thead>
            <tbody>
              {types.map(gt => (
                <tr key={gt.id} className="border-t border-[#EEF1F5]">
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{gt.key}</td>
                  <td className="px-4 py-2">
                    <input defaultValue={gt.icon || ""} onBlur={e => e.target.value !== gt.icon && saveType(gt, { icon: e.target.value })}
                      className="w-14 px-2 py-1 rounded-lg border border-gray-200 text-center outline-none focus:border-brand" />
                  </td>
                  <td className="px-4 py-2">
                    <input defaultValue={gt.label} onBlur={e => e.target.value !== gt.label && saveType(gt, { label: e.target.value })}
                      className="w-full max-w-[160px] px-2 py-1 rounded-lg border border-gray-200 outline-none focus:border-brand" />
                  </td>
                  <td className="px-4 py-2">
                    <input type="number" defaultValue={gt.sort_order} onBlur={e => +e.target.value !== gt.sort_order && saveType(gt, { sort_order: +e.target.value })}
                      className="w-16 px-2 py-1 rounded-lg border border-gray-200 outline-none focus:border-brand" />
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={() => saveType(gt, { is_enabled: !gt.is_enabled })}
                      className={`text-xs font-semibold px-2 py-1 rounded-full ${gt.is_enabled ? "bg-[#DFF5E6] text-[#22874E] hover:bg-green-200" : "bg-red-100 text-red-600 hover:bg-red-200"}`}>
                      {gt.is_enabled ? "Enabled" : "Hidden"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ───────────── PROMO CAMPAIGNS ─────────────
   Partner marketing codes ("sign up with PARTNERX, get a free month") for
   both distribution channels: a promo video (video_url) for online/
   influencer campaigns, or a plain code with no video for physical
   printed coupons handed out at events — video_url is optional either way.
   max_redemptions is required: an uncapped code that leaks publicly has
   no ceiling on how much free subscription time it can give away. */
const BLANK_CAMPAIGN = { partner_name: "", code: "", video_url: "", reward_days: 30, max_redemptions: 100 };

function PromoCampaigns({ auth }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(BLANK_CAMPAIGN);
  const [creating, setCreating]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/kids/promo-campaigns`, auth);
      setCampaigns(r.data.data || []);
    } catch { toast.error("Failed to load promo campaigns."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCampaign = async () => {
    if (!form.partner_name.trim() || !form.code.trim()) return toast.warn("Partner name and code are required.");
    setCreating(true);
    try {
      await axios.post(`${API}/api/edolutions/admin/kids/promo-campaigns`, form, auth);
      toast.success("Promo campaign created.");
      setForm(BLANK_CAMPAIGN);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't create campaign."); }
    finally { setCreating(false); }
  };

  const toggleCampaign = async (c) => {
    try {
      await axios.put(`${API}/api/edolutions/admin/kids/promo-campaigns/${c.id}/toggle`, {}, auth);
      load();
    } catch { toast.error("Couldn't update campaign."); }
  };

  if (loading) return <SkTable cols={6} rows={4} />;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4">
        <p className="font-display font-semibold text-navy mb-1">🎁 Partner Promo Campaigns</p>
        <p className="text-sm text-gray-600">
          Give a code to a partner (a video creator, a school, a printed coupon at an event) — anyone who
          signs up with it gets a free trial extension, capped by <b>max redemptions</b> so a leaked code
          can't be used without limit. Each account can redeem <b>one</b> promo code, ever.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F8FA] text-gray-500 text-start">
            <tr>
              <th className="px-4 py-3">Partner</th><th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Reward</th><th className="px-4 py-3">Redeemed</th>
              <th className="px-4 py-3">Video</th><th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(c => (
              <tr key={c.id} className="border-t border-[#EEF1F5]">
                <td className="px-4 py-2 font-semibold text-gray-700">{c.partner_name}</td>
                <td className="px-4 py-2 font-mono text-xs">{c.code}</td>
                <td className="px-4 py-2 text-gray-600">{c.reward_days} days free</td>
                <td className="px-4 py-2 text-gray-600">{c.redemptions_count} / {c.max_redemptions}</td>
                <td className="px-4 py-2 text-gray-400">{c.video_url ? "🎬 yes" : "— coupon only"}</td>
                <td className="px-4 py-2">
                  <button onClick={() => toggleCampaign(c)}
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${c.is_active ? "bg-[#DFF5E6] text-[#22874E] hover:bg-green-200" : "bg-red-100 text-red-600 hover:bg-red-200"}`}>
                    {c.is_active ? "Active" : "Disabled"}
                  </button>
                </td>
              </tr>
            ))}
            {campaigns.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No promo campaigns yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-4">
        <p className="text-xs font-semibold text-gray-500 mb-3">+ New promo campaign</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Partner name (e.g. Ali's Kids Show)" value={form.partner_name}
            onChange={e => setForm(f => ({ ...f, partner_name: e.target.value }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-brand" />
          <input placeholder="Code (e.g. ALI30)" value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono outline-none focus:border-brand" />
          <input placeholder="Promo video URL (optional — leave blank for coupon-only)" value={form.video_url}
            onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
            className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-brand" />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Reward (days free)
            <input type="number" min={1} value={form.reward_days}
              onChange={e => setForm(f => ({ ...f, reward_days: +e.target.value }))}
              className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 outline-none focus:border-brand" />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            Max redemptions
            <input type="number" min={1} value={form.max_redemptions}
              onChange={e => setForm(f => ({ ...f, max_redemptions: +e.target.value }))}
              className="w-24 px-2 py-1.5 rounded-lg border border-gray-200 outline-none focus:border-brand" />
          </label>
        </div>
        <button onClick={createCampaign} disabled={creating}
          className="mt-3 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-semibold px-4 py-2 rounded-xl text-sm">
          {creating ? "Creating…" : "Create campaign"}
        </button>
      </div>
    </div>
  );
}

/* ───────────── GAMES ───────────── */
const BLANK_GAME = {
  name: "", slug: "", subject: "math", min_grade: 0, max_grade: 6,
  difficulty: "easy", game_type: "quiz", xp_reward: 10, coin_reward: 5,
  plan_required: "starter", is_active: true, content: null,
};

// Ready-to-edit content examples per playable game type. `ans` is the 0-based
// index of the correct option. Only quiz / drag_drop / typing are playable.
const CONTENT_TEMPLATES = {
  quiz: {
    emoji: "🧮",
    timer: 15,
    questions: [
      { q: "What is 5 + 3?",  opts: ["6", "7", "8", "9"], ans: 2, tip: "Count up from 5!" },
      { q: "What is 10 - 4?", opts: ["4", "5", "6", "7"], ans: 2 },
    ],
  },
  drag_drop: {
    type: "count",
    emoji: "🔢",
    instruction: "Tap a number, then tap the matching group!",
    rounds: [
      { items: ["1", "2", "3"], targets: [
        { display: "🍎",       match: "1" },
        { display: "🌟🌟",     match: "2" },
        { display: "🎈🎈🎈",   match: "3" },
      ]},
    ],
  },
  "drag_drop:word": {
    type: "word",
    emoji: "🔤",
    instruction: "Tap the letters to spell the word!",
    rounds: [
      { word: "CAT", hint: "🐱", scrambled: ["C", "A", "T"] },
      { word: "DOG", hint: "🐶", scrambled: ["D", "O", "G"] },
    ],
  },
  typing: {
    deck: [
      { letter: "A", emoji: "🍎", word: "Apple" },
      { letter: "B", emoji: "🦋", word: "Butterfly" },
      { letter: "C", emoji: "🐱", word: "Cat" },
    ],
  },
  "puzzle:memory": {
    puzzle_type: "memory",
    emoji: "🧠",
    pairs: [
      { emoji: "🍎", word: "Apple" },
      { emoji: "🍌", word: "Banana" },
      { emoji: "🚗", word: "Car" },
      { emoji: "🐶", word: "Dog" },
      { emoji: "🌍", word: "Earth" },
      { emoji: "🐟", word: "Fish" },
    ],
  },
  "puzzle:jigsaw": {
    puzzle_type: "tile",
    emoji: "🧩",
    grid_cols: 4,
    grid_rows: 3,
    image: "https://example.com/your-puzzle-image.jpg",
    title: "My Puzzle",
  },
  memory: {
    emoji: "🐾",
    bg: "linear-gradient(135deg, #14b8a6 0%, #0891b2 100%)",
    accent: "#0891b2",
    peek_seconds: 6,
    pairs: [
      { emoji: "🐦", word: "Bird",      color: "blue"   },
      { emoji: "🦉", word: "Owl",       color: "blue"   },
      { emoji: "🐐", word: "Goat",      color: "yellow" },
      { emoji: "🦋", word: "Butterfly", color: "yellow" },
      { emoji: "🐭", word: "Mouse",     color: "red"    },
      { emoji: "🦔", word: "Hedgehog",  color: "red"    },
      { emoji: "🦊", word: "Fox",       color: "blue"   },
      { emoji: "🌸", word: "Flower",    color: "yellow" },
      { emoji: "🐠", word: "Fish",      color: "red"    },
    ],
  },
  connect_lines: {
    emoji: "🔗",
    instruction: "Tap a letter, then tap its matching pair!",
    pairs: [
      { left: "A", right: "a" },
      { left: "B", right: "b" },
      { left: "C", right: "c" },
      { left: "D", right: "d" },
    ],
  },
  flashcard: {
    title: "My First Shapes",
    emoji: "🔷",
    bg: "radial-gradient(130% 100% at 50% 0%, #9B7BFF 0%, #6A47E0 55%, #5636C4 100%)",
    accent: "#7c3aed",
    vehicles: [
      { name: "Circle",   emoji: "⚪", category: "round",   fact: "A circle is perfectly round — every edge is the same distance from the middle!" },
      { name: "Square",   emoji: "🟦", category: "straight", fact: "A square has 4 equal sides and 4 corners!" },
      { name: "Triangle", emoji: "🔺", category: "straight", fact: "A triangle has 3 sides and 3 corners!" },
      { name: "Star",     emoji: "⭐", category: "round",   fact: "Stars have points called vertices — this one has 5!" },
    ],
    categories: {
      round:    { label: "Round",    emoji: "⚪", color: "from-blue-500 to-indigo-600",  accent: "#6366f1" },
      straight: { label: "Straight", emoji: "🟦", color: "from-orange-500 to-amber-600", accent: "#f59e0b" },
    },
  },
  tracing: {
    emoji: "🍎",
    letters: [
      { letter: "A", name: "A", word: "Apple", wordEn: "Apple", emoji: "🍎" },
      { letter: "B", name: "B", word: "Banana", wordEn: "Banana", emoji: "🍌" },
      { letter: "C", name: "C", word: "Cat", wordEn: "Cat", emoji: "🐱" },
    ],
  },
  smoothie: {
    title: "Smoothie Stand",
    emoji: "🥤",
    bg: "radial-gradient(130% 100% at 50% 0%, #34D5C6 0%, #12B8A8 55%, #0E8F82 100%)",
    accent: "#12B8A8",
    fruits: [
      { key: "strawberry", name: "Strawberry", emoji: "🍓", color: "#F2506E" },
      { key: "banana",     name: "Banana",     emoji: "🍌", color: "#F5D019" },
      { key: "mango",      name: "Mango",      emoji: "🥭", color: "#F5A623" },
      { key: "kiwi",       name: "Kiwi",       emoji: "🥝", color: "#7CB518" },
    ],
    orders: [
      { want: ["strawberry", "banana"], customer: "I'd love a strawberry-banana blend, pretty please!" },
      { want: ["mango", "kiwi"],        customer: "Something tropical, please!" },
    ],
  },
};

function Games({ auth }) {
  const { t } = useTranslation();
  const [games, setGames]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // game object or BLANK_GAME

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/kids/games`, auth);
      setGames(r.data.data || []);
    } catch { toast.error("Failed to load games."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (g) => {
    try {
      await axios.put(`${API}/api/edolutions/admin/kids/games/${g.id}/toggle`, {}, auth);
      load();
    } catch { toast.error("Couldn't toggle game."); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? (If it has play history it will be deactivated instead.)`)) return;
    try {
      const r = await axios.delete(`${API}/api/edolutions/admin/kids/games/${g.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't delete game."); }
  };

  if (loading) return <SkTable cols={8} rows={6} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="font-extrabold text-[15px] text-navy">All games</div>
        <button onClick={() => setEditing({ ...BLANK_GAME })}
          className="bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2 rounded-xl text-sm">{t("admin.games.newGame")}</button>
      </div>

      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F8FA] text-[#8A93A5] text-start">
            <tr>
              <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-wide">{t("admin.games.name")}</th><th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-wide">{t("admin.games.subject")}</th>
              <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-wide">{t("admin.games.grades")}</th><th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-wide">{t("admin.games.type")}</th>
              <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-wide">{t("admin.games.rewards")}</th><th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-wide">{t("admin.games.plan")}</th>
              <th className="px-4 py-3 text-[11px] font-extrabold uppercase tracking-wide">{t("admin.games.active")}</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {games.map(g => (
              <tr key={g.id} className="border-t border-[#EEF1F5]">
                <td className="px-4 py-3 font-extrabold text-ink">{g.name}<div className="text-xs text-gray-400 font-normal">{g.slug}</div></td>
                <td className="px-4 py-3 capitalize text-gray-600">{g.subject}</td>
                <td className="px-4 py-3 text-gray-600">{g.min_grade}–{g.max_grade}</td>
                <td className="px-4 py-3">
                  <span className="text-[11px] font-bold bg-[#EAF1FF] text-[#3F6FD6] px-2.5 py-1 rounded-lg">{g.game_type}</span>
                </td>
                <td className="px-4 py-3 text-gray-600">⭐{g.xp_reward} / 🪙{g.coin_reward}</td>
                <td className="px-4 py-3 capitalize text-gray-600">{g.plan_required}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggle(g)}
                    className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${g.is_active ? "bg-[#DFF5E6] text-[#22874E]" : "bg-[#F1F2F5] text-[#8A93A5]"}`}>
                    {g.is_active ? t("admin.common.on") : t("admin.common.off")}
                  </button>
                </td>
                <td className="px-4 py-3 text-end whitespace-nowrap">
                  <button onClick={() => setEditing(g)} className="text-navy font-semibold hover:underline me-3">{t("admin.games.edit")}</button>
                  <button onClick={() => remove(g)} className="text-red-500 font-semibold hover:underline">{t("admin.games.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <GameModal game={editing} auth={auth} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function GameModal({ game, auth, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...game });
  // The puzzle jigsaw `image` field is a base64 data URI that can run
  // 100KB-500KB+ — embedding it in the visible JSON textarea made the box
  // effectively unusable (a single half-megabyte line, laggy on every
  // keystroke, the actual editable fields buried inside it). Keep it out of
  // the textarea entirely; it's tracked in `puzzleImage` and shown as a
  // thumbnail instead, then merged back into content on save.
  const { image: initialPuzzleImage, ...contentWithoutImage } = game.content || {};
  const [contentText, setContentText] = useState(
    Object.keys(contentWithoutImage).length ? JSON.stringify(contentWithoutImage, null, 2) : ""
  );
  const [puzzleImage, setPuzzleImage] = useState(initialPuzzleImage || null);
  const [quizTimer, setQuizTimer] = useState(Number(game.content?.timer) > 0 ? Number(game.content.timer) : 15);
  const [saving, setSaving] = useState(false);

  // AI generation / file import / image import
  const [aiTopic, setAiTopic] = useState("");
  const [aiCount, setAiCount] = useState(5);
  const [aiBusy, setAiBusy]   = useState(false);
  const [sourceText, setSourceText] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  const isNew = !game.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Dynamic subject/game-type key lists (falls back to the built-in set if
  // the taxonomy endpoints are unreachable, so game creation never blocks).
  const [subjectKeys, setSubjectKeys] = useState(SUBJECTS);
  const [typeKeys, setTypeKeys]       = useState(GAME_TYPES);
  useEffect(() => {
    axios.get(`${API}/api/edolutions/admin/kids/subjects`, auth)
      .then(r => { const keys = (r.data.data || []).filter(s => s.is_active).map(s => s.key); if (keys.length) setSubjectKeys(keys); })
      .catch(() => {});
    axios.get(`${API}/api/edolutions/admin/kids/game-types`, auth)
      .then(r => { const keys = (r.data.data || []).map(g => g.key); if (keys.length) setTypeKeys(keys); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    let content = null;
    if (contentText.trim()) {
      try { content = JSON.parse(contentText); }
      catch { return toast.error("Content is not valid JSON."); }
    }
    // Quizzes carry their countdown inside content.timer.
    if (form.game_type === "quiz" && content && typeof content === "object") {
      content.timer = Number(quizTimer) > 0 ? Number(quizTimer) : 15;
    }
    // Puzzle image lives in its own state (kept out of the JSON textarea —
    // see comment above) — merge it back in only for puzzle games that
    // actually have one.
    if (form.game_type === "puzzle" && puzzleImage) {
      content = { ...(content || { puzzle_type: "tile", grid_cols: 4, grid_rows: 3 }), image: puzzleImage };
    }
    setSaving(true);
    try {
      const payload = { ...form, content };
      if (isNew) await axios.post(`${API}/api/edolutions/admin/kids/games`, payload, auth);
      else       await axios.put(`${API}/api/edolutions/admin/kids/games/${game.id}`, payload, auth);
      toast.success(isNew ? "Game created." : "Game updated.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save game.");
    } finally { setSaving(false); }
  };

  // Generate game content with Claude (from topic, file, or image).
  const generate = async (useSource) => {
    if (aiBusy) return;
    if (!useSource && !aiTopic.trim()) return toast.warn("Enter a topic to generate from.");
    if (useSource && !sourceText.trim() && !imagePreview) return toast.warn("Upload a file or image first.");
    setAiBusy(true);
    try {
      const body = {
        count: Number(aiCount) || 5,
        grade: form.min_grade ?? 2,
        difficulty: form.difficulty || "easy",
        subject: form.subject || "general",
        game_type: form.game_type || "quiz",
      };
      if (aiTopic.trim()) body.topic = aiTopic.trim();
      if (useSource && sourceText) body.source_text = sourceText.slice(0, 20000);
      if (useSource && imagePreview) body.image_base64 = imagePreview;

      const res = await axios.post(`${API}/api/edolutions/admin/kids/games/generate`, body, {
        ...auth,
        maxContentLength: 15000000,
        maxBodyLength: 15000000,
      });
      const content = res.data?.data?.content;
      const returnedType = res.data?.data?.game_type;
      const aiUsage = res.data?.data?.ai_usage;
      if (content && typeof content === "object") {
        setContentText(JSON.stringify(content, null, 2));
        if (Number(content.timer) > 0) setQuizTimer(Number(content.timer));
        if (returnedType && returnedType !== form.game_type) set("game_type", returnedType);
        const usageMsg = aiUsage ? ` (${aiUsage.input_tokens + aiUsage.output_tokens} tokens · ~$${aiUsage.cost_estimate})` : "";
        toast.success(`Game content generated!${usageMsg} Review, then Save.`);
      } else {
        toast.error("No usable content returned. Try again.");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "AI generation failed.");
    } finally { setAiBusy(false); }
  };

  // Read an uploaded file: .json loads straight into content; anything else
  // (txt/csv/md) becomes source material the AI turns into questions.
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setSourceName(file.name);
      if (file.name.toLowerCase().endsWith(".json")) {
        try { JSON.parse(text); setContentText(text); setSourceText(""); toast.success("JSON loaded into Content."); }
        catch { toast.error("That .json file isn't valid JSON."); }
      } else {
        setSourceText(text);
        toast.success(`Loaded "${file.name}". Click "Generate from file".`);
      }
    };
    reader.readAsText(file);
    e.target.value = ""; // allow re-uploading the same file
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-semibold text-lg text-navy mb-4">{isNew ? "New game" : `Edit: ${game.name}`}</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <L label={t("admin.games.name")}><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
          <L label="Slug (optional — auto if blank)"><input className={inp} value={form.slug || ""} onChange={e => set("slug", e.target.value)} /></L>
          <L label={t("admin.games.subject")}><Sel value={form.subject} opts={subjectKeys} onChange={v => set("subject", v)} /></L>
          <L label="Game type"><Sel value={form.game_type} opts={typeKeys} onChange={v => set("game_type", v)} /></L>
          <L label="Min grade"><input type="number" className={inp} value={form.min_grade} onChange={e => set("min_grade", +e.target.value)} /></L>
          <L label="Max grade"><input type="number" className={inp} value={form.max_grade} onChange={e => set("max_grade", +e.target.value)} /></L>
          <L label="Difficulty"><Sel value={form.difficulty} opts={DIFFS} onChange={v => set("difficulty", v)} /></L>
          <L label="Plan required"><Sel value={form.plan_required} opts={PLANS} onChange={v => set("plan_required", v)} /></L>
          <L label="XP reward"><input type="number" className={inp} value={form.xp_reward} onChange={e => set("xp_reward", +e.target.value)} /></L>
          <L label="Coin reward"><input type="number" className={inp} value={form.coin_reward} onChange={e => set("coin_reward", +e.target.value)} /></L>
        </div>

        <div className="flex flex-wrap items-center gap-6 mt-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} />
            Active (visible to kids)
          </label>
          {form.game_type === "quiz" && (
            <label className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-gray-600">⏱ Timer per question (seconds)</span>
              <input type="number" value={quizTimer}
                onChange={e => setQuizTimer(e.target.value)}
                className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 outline-none focus:border-brand" />
            </label>
          )}
        </div>

        {/* ── AI generate / file import / image import ── */}
        <div className="mt-5 rounded-xl bg-[#F6F2FF] border border-[#E4DAFB] p-4">
          <p className="font-display font-semibold text-navy mb-1">✨ Generate game content with AI</p>
          <p className="text-xs text-[#8A80B0] mb-3">
            Describe a topic, upload a text file, or <b>snap a photo</b> of a textbook page — AI generates game content automatically.
          </p>
          <div className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
            <L label="Topic / prompt">
              <input className="w-full px-3 py-2 rounded-xl border border-[#E0D6F5] bg-white outline-none focus:border-aiaccent text-sm" value={aiTopic} onChange={e => setAiTopic(e.target.value)}
                placeholder="e.g. Addition up to 20, vehicles, fruits, Urdu letters…" />
            </L>
            <L label="Count">
              <input type="number" min={1} max={40} className="w-24 px-3 py-2 rounded-xl border border-[#E0D6F5] bg-white outline-none focus:border-aiaccent text-sm" value={aiCount}
                onChange={e => setAiCount(e.target.value)} />
            </L>
            <button type="button" onClick={() => generate(false)} disabled={aiBusy}
              className="h-[42px] px-4 rounded-xl text-sm font-semibold bg-aiaccent hover:opacity-90 disabled:opacity-50 text-white whitespace-nowrap">
              {aiBusy ? t("admin.games.generating") : t("admin.games.generate")}
            </button>
          </div>
          <p className="text-xs text-[#8A80B0] mt-2">
            Count supports up to 40 — for a tracing alphabet, use the alphabet's real letter count (e.g. 26 for English, 36 for Urdu).
          </p>

          {/* Image upload */}
          <div className="mt-3 p-3 rounded-lg bg-white border border-[#E4DAFB]">
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:opacity-90 cursor-pointer flex items-center gap-1.5">
                📷 Upload Image / PDF
                <input type="file" accept="image/*,.pdf" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB."); return; }
                  const reader = new FileReader();
                  reader.onload = () => { setImagePreview(reader.result); setSourceName(file.name); toast.success(`Image loaded: ${file.name}`); };
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }} className="hidden" />
              </label>
              <label className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-white border border-[#E0D6F5] text-[#6A5DA0] hover:bg-[#F6F2FF] cursor-pointer">
                📎 Upload text file
                <input type="file" accept=".txt,.csv,.md,.json,.text" onChange={onFile} className="hidden" />
              </label>
              {sourceName && <span className="text-xs text-gray-500">Loaded: <b>{sourceName}</b></span>}
            </div>

            {imagePreview && (
              <div className="mt-2 relative inline-block">
                <img src={imagePreview} alt="Uploaded" className="max-h-40 rounded-lg border border-gray-200 shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)]" />
                <button onClick={() => { setImagePreview(null); setSourceName(""); }}
                  className="absolute -top-2 -end-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs font-bold hover:bg-red-600">✕</button>
              </div>
            )}

            {(imagePreview || sourceText) && (
              <div className="mt-2">
                <button type="button" onClick={() => generate(true)} disabled={aiBusy}
                  className="text-xs font-semibold px-4 py-2 rounded-lg bg-aiaccent hover:opacity-90 disabled:opacity-50 text-white">
                  {aiBusy ? "Generating…" : imagePreview ? "✨ Generate from image" : "✨ Generate from file"}
                </button>
              </div>
            )}
          </div>

          <p className="text-[11px] text-[#A0A8B5] mt-2">
            📷 <b>Image</b>: Upload a photo of a textbook, worksheet, or flashcard book — AI reads it and creates game content.
            📎 <b>.json</b> files load straight into Content. Other text files become source material. Always review the result before saving.
          </p>
        </div>

        {/* Puzzle Image Upload */}
        {form.game_type === "puzzle" && (
          <div className="mt-5 rounded-xl bg-purple-50 border border-purple-100 p-4">
            <p className="font-display font-semibold text-navy mb-1">🧩 Puzzle Image</p>
            <p className="text-xs text-gray-500 mb-3">Upload an image for the jigsaw puzzle. The image will be split into a grid for kids to reassemble.</p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-xs font-semibold px-3 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 cursor-pointer flex items-center gap-1.5">
                🖼️ {puzzleImage ? "Replace Puzzle Image" : "Upload Puzzle Image"}
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB."); return; }
                  const reader = new FileReader();
                  reader.onload = () => {
                    // Ensure the grid fields exist in the (small, editable)
                    // JSON box even though the image itself lives separately.
                    try {
                      const c = contentText.trim() ? JSON.parse(contentText) : {};
                      setContentText(JSON.stringify({
                        puzzle_type: c.puzzle_type || "tile",
                        grid_cols: c.grid_cols || 4,
                        grid_rows: c.grid_rows || 3,
                        ...c,
                      }, null, 2));
                    } catch { /* leave contentText as-is if it wasn't valid JSON yet */ }
                    setPuzzleImage(reader.result);
                    toast.success("Puzzle image updated!");
                  };
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }} className="hidden" />
              </label>
              {puzzleImage && (
                <button type="button" onClick={() => setPuzzleImage(null)}
                  className="text-xs font-semibold px-3 py-2 rounded-lg bg-white border border-red-200 text-red-500 hover:bg-red-50">
                  ✕ Remove image
                </button>
              )}
              <div className="text-xs text-gray-400">
                Or paste an image URL directly in the Content JSON under <code>"image"</code>
              </div>
            </div>
            {puzzleImage && (
              <div className="mt-3">
                <img src={puzzleImage} alt="Puzzle preview" className="max-h-32 rounded-lg border border-purple-200 shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)]" />
                {(() => {
                  try {
                    const c = contentText.trim() ? JSON.parse(contentText) : {};
                    return <p className="text-[10px] text-gray-400 mt-1">Grid: {c.grid_cols || 4} x {c.grid_rows || 3} = {(c.grid_cols || 4) * (c.grid_rows || 3)} pieces</p>;
                  } catch { return null; }
                })()}
              </div>
            )}
          </div>
        )}

        {/* How to create a game */}
        <div className="mt-5 rounded-xl bg-orange-50 border border-orange-100 p-4 text-sm text-gray-600">
          <p className="font-display font-semibold text-navy mb-1">🎮 How to create a playable game</p>
          <ol className="list-decimal ms-5 space-y-1">
            <li>Fill in the details above and pick a <b>Game type</b> of <b>quiz</b>, <b>drag_drop</b>, <b>typing</b>, <b>flashcard</b>, <b>tracing</b>, <b>memory</b>, <b>connect_lines</b>, <b>smoothie</b>, or <b>puzzle</b>.</li>
            <li>Click an <b>“Insert example”</b> button below to drop a working <b>Content</b> template into the box.</li>
            <li>Edit the questions / rounds / letters to your liking, then <b>Save game</b>.</li>
            <li>Set the game <b>Active</b> so it appears for kids of the chosen grade range.</li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            Tip: for quizzes, <code>ans</code> is the position of the correct answer starting at <b>0</b>
            (so <code>ans: 2</code> = the third option), and the <b>⏱ Timer</b> field above sets the countdown per question.
            drawing / story types are not playable yet.
          </p>
        </div>

        <div className="mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <label className="block text-sm font-semibold text-gray-600">Content (JSON)</label>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.quiz, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-orange-200 text-brand hover:bg-orange-100">Insert example: quiz</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.drag_drop, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-orange-200 text-brand hover:bg-orange-100">drag: count</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES["drag_drop:word"], null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-orange-200 text-brand hover:bg-orange-100">drag: word</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.typing, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-orange-200 text-brand hover:bg-orange-100">typing</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES["puzzle:memory"], null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-purple-200 text-purple-600 hover:bg-purple-100">puzzle: memory</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES["puzzle:jigsaw"], null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-purple-200 text-purple-600 hover:bg-purple-100">puzzle: jigsaw</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.memory, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-teal-200 text-teal-600 hover:bg-teal-50">🧠 memory</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.connect_lines, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-green-200 text-green-600 hover:bg-green-50">🔗 connect lines</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.flashcard, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-violet-200 text-violet-600 hover:bg-violet-50">🚗 flashcard</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.tracing, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-pink-200 text-pink-600 hover:bg-pink-50">✏️ tracing</button>
              <button type="button" onClick={() => setContentText(JSON.stringify(CONTENT_TEMPLATES.smoothie, null, 2))}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-white border border-cyan-200 text-cyan-600 hover:bg-cyan-50">🥤 smoothie</button>
            </div>
          </div>
          <textarea rows={10} value={contentText} onChange={e => setContentText(e.target.value)}
            placeholder='Click an "Insert example" button above to start, or generate content with AI further up.'
            className="w-full px-3 py-2 rounded-xl border border-gray-200 font-mono text-xs outline-none focus:border-brand" />
          <p className="text-xs text-gray-400 mt-1">
            Must be valid JSON matching the chosen game type (use the example buttons as a guide) — required for every type except <b>drawing</b> and <b>story</b>, which have no player yet.
          </p>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100">{t("admin.common.cancel")}</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl text-sm font-semibold bg-brand hover:bg-brand-hover disabled:opacity-50 text-white">
            {saving ? t("admin.games.saving") : t("admin.games.saveGame")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────── PLANS (pricing) ───────────── */
const FEATURE_KEYS = ["games", "ai_mentor", "live_classes", "reports"];
const FEATURE_LABELS = { games: "Games", ai_mentor: "AI Mentor", live_classes: "Live classes", reports: "Reports" };
const BLANK_PLAN = {
  name: "", slug: "", price_monthly: 0, price_yearly: 0, trial_days: 14,
  max_children: 1, features: { games: true, ai_mentor: false, live_classes: false, reports: true }, is_active: true,
};

function Plans({ auth }) {
  const { t } = useTranslation();
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/kids/plans`, auth);
      setPlans(r.data.data || []);
    } catch { toast.error("Failed to load plans."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    try { await axios.put(`${API}/api/edolutions/admin/kids/plans/${p.id}/toggle`, {}, auth); load(); }
    catch { toast.error("Couldn't toggle plan."); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? (If it has subscribers it will be deactivated instead.)`)) return;
    try {
      const r = await axios.delete(`${API}/api/edolutions/admin/kids/plans/${p.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch { toast.error("Couldn't delete plan."); }
  };

  if (loading) return <SkCardGrid count={3} cols="sm:grid-cols-2 lg:grid-cols-3" />;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...BLANK_PLAN })}
          className="bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2 rounded-xl text-sm">{t("admin.plans.newPlan")}</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map(p => (
          <div key={p.id} className={`bg-white rounded-2xl border shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 flex flex-col ${p.is_active ? "border-[#EEF1F5]" : "border-red-200 opacity-70"}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-bold text-navy">{p.name}</div>
              <button onClick={() => toggle(p)}
                className={`text-xs font-semibold px-2 py-1 rounded-full ${p.is_active ? "bg-[#DFF5E6] text-[#22874E]" : "bg-gray-100 text-gray-500"}`}>
                {p.is_active ? t("admin.common.on") : t("admin.common.off")}
              </button>
            </div>
            <div className="text-2xl font-extrabold text-brand">${p.price_monthly}<span className="text-xs text-gray-400 font-medium">/mo</span></div>
            <div className="text-sm text-gray-500">${p.price_yearly}/yr · {p.trial_days}-day trial</div>
            <div className="text-xs text-gray-500 mt-1">Up to {p.max_children} child{p.max_children > 1 ? "ren" : ""}</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {FEATURE_KEYS.filter(k => p.features?.[k]).map(k => (
                <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-brand">{FEATURE_LABELS[k]}</span>
              ))}
            </div>
            <div className="text-xs text-gray-400 mt-2">{p.active_subscribers ?? 0} active subscriber{(p.active_subscribers ?? 0) === 1 ? "" : "s"}</div>
            <div className="flex gap-3 mt-3 pt-3 border-t border-[#EEF1F5]">
              <button onClick={() => setEditing(p)} className="text-navy text-sm font-semibold hover:underline">{t("admin.games.edit")}</button>
              <button onClick={() => remove(p)} className="text-red-500 text-sm font-semibold hover:underline">{t("admin.games.delete")}</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <PlanModal plan={editing} auth={auth} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PlanModal({ plan, auth, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...plan, features: { ...(plan.features || {}) } });
  const [saving, setSaving] = useState(false);
  const isNew = !plan.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFeature = (k, v) => setForm(f => ({ ...f, features: { ...f.features, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_monthly: +form.price_monthly,
        price_yearly:  +form.price_yearly,
        trial_days:    +form.trial_days,
        max_children:  +form.max_children,
      };
      if (isNew) await axios.post(`${API}/api/edolutions/admin/kids/plans`, payload, auth);
      else       await axios.put(`${API}/api/edolutions/admin/kids/plans/${plan.id}`, payload, auth);
      toast.success(isNew ? "Plan created." : "Plan updated.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save plan.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-semibold text-lg text-navy mb-4">{isNew ? "New plan" : `Edit: ${plan.name}`}</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <L label={t("admin.games.name")}><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
          <L label="Slug (optional — auto if blank)"><input className={inp} value={form.slug || ""} onChange={e => set("slug", e.target.value)} /></L>
          <L label="Monthly price ($)"><input type="number" step="0.01" className={inp} value={form.price_monthly} onChange={e => set("price_monthly", e.target.value)} /></L>
          <L label="Yearly price ($)"><input type="number" step="0.01" className={inp} value={form.price_yearly} onChange={e => set("price_yearly", e.target.value)} /></L>
          <L label="Trial days"><input type="number" className={inp} value={form.trial_days} onChange={e => set("trial_days", e.target.value)} /></L>
          <L label="Max children"><input type="number" className={inp} value={form.max_children} onChange={e => set("max_children", e.target.value)} /></L>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-600 mb-2">Features included</label>
          <div className="grid grid-cols-2 gap-2">
            {FEATURE_KEYS.map(k => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.features?.[k]} onChange={e => setFeature(k, e.target.checked)} />
                {FEATURE_LABELS[k]}
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm">
          <input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} />
          Active (offered to parents)
        </label>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100">{t("admin.common.cancel")}</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl text-sm font-semibold bg-brand hover:bg-brand-hover disabled:opacity-50 text-white">
            {saving ? t("admin.plans.saving") : t("admin.plans.savePlan")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────── SUBSCRIPTION REQUESTS ───────────── */
function Requests({ auth }) {
  const { t } = useTranslation();
  const [reqs, setReqs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("pending");
  const [busy, setBusy]       = useState(null);

  const load = useCallback(async (status = "pending") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/kids/subscription-requests?status=${status}`, auth);
      setReqs(r.data.data || []);
    } catch { toast.error("Failed to load requests."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const act = async (id, action) => {
    let admin_note = null;
    if (action === "reject") {
      admin_note = window.prompt("Reason for rejection (optional):", "") ?? null;
    }
    setBusy(id);
    try {
      const r = await axios.post(`${API}/api/edolutions/admin/kids/subscription-requests/${id}/${action}`, { admin_note }, auth);
      toast.success(r.data.message);
      load(filter);
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't update request."); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", "all"].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`text-sm font-semibold px-3 py-1.5 rounded-full capitalize ${filter === s ? "bg-navy text-white" : "bg-gray-100 text-gray-500"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? <SkTable cols={6} rows={5} /> : reqs.length === 0 ? <Empty text={t("admin.requests.noRequests")} /> : (
        <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA] text-gray-500 text-start">
              <tr>
                <th className="px-4 py-3">Parent</th><th className="px-4 py-3">Request</th>
                <th className="px-4 py-3">Cycle</th><th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reqs.map(r => (
                <tr key={r.id} className="border-t border-[#EEF1F5] align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{r.parent_name}</div>
                    <div className="text-xs text-gray-400">{r.parent_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {r.type === "cancel"
                      ? <span className="text-red-600 font-semibold">{t("admin.requests.cancelSubscription")}</span>
                      : <span><span className="text-gray-400">{r.current_plan || "—"}</span> → <span className="font-semibold text-brand">{r.requested_plan}</span></span>}
                    {r.note && <div className="text-xs text-gray-400 mt-1">“{r.note}”</div>}
                    {r.admin_note && <div className="text-xs text-gray-400 mt-1">Admin: {r.admin_note}</div>}
                  </td>
                  <td className="px-4 py-3 capitalize">{r.billing_cycle}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{r.created_at}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      r.status === "pending" ? "bg-yellow-100 text-yellow-700"
                      : r.status === "approved" ? "bg-[#DFF5E6] text-[#22874E]"
                      : "bg-gray-100 text-gray-500"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-end whitespace-nowrap">
                    {r.status === "pending" && (
                      <>
                        <button onClick={() => act(r.id, "approve")} disabled={busy === r.id}
                          className="text-green-600 font-semibold hover:underline me-3 disabled:opacity-50">{t("admin.requests.approve")}</button>
                        <button onClick={() => act(r.id, "reject")} disabled={busy === r.id}
                          className="text-red-500 font-semibold hover:underline disabled:opacity-50">{t("admin.requests.reject")}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ───────────── TRIAL LEADS ───────────── */
const LEAD_GRADES = ["KG", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6"];

function TrialLeads({ auth }) {
  const [leads, setLeads]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/edolutions/admin/kids/guest-leads`, auth)
      .then(r => setLeads(r.data.data || []))
      .catch(() => toast.error("Failed to load trial leads."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Parent", "Email", "Phone", "Grade", "Signed up", "Tried on"]].concat(
      leads.map(l => [l.parent_name, l.email, l.phone, LEAD_GRADES[l.child_grade] ?? l.child_grade, l.signed_up ? "Yes" : "No", l.created_at]));
    const blob = new Blob([rows.map(r => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "cabil-kids-trial-leads.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500">Parents who filled the “Try it now” form on cabil.co/kids. {leads.length > 0 && <b>{leads.length} total · {leads.filter(l => l.signed_up).length} signed up</b>}</p>
        {leads.length > 0 && (
          <button onClick={exportCsv} className="text-sm font-semibold px-3 py-1.5 rounded-full bg-navy text-white whitespace-nowrap">Export CSV</button>
        )}
      </div>

      {loading ? <SkTable cols={6} rows={5} /> : leads.length === 0 ? <Empty text="No trial leads yet." /> : (
        <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F8FA] text-gray-500 text-start">
              <tr>
                <th className="px-4 py-3">Parent</th><th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Grade</th><th className="px-4 py-3">Tried on</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l.id} className="border-t border-[#EEF1F5] align-top">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-800">{l.parent_name}</div>
                    <a href={`mailto:${l.email}`} className="text-xs text-gray-400 hover:underline">{l.email}</a>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{l.phone || "—"}</td>
                  <td className="px-4 py-3">{LEAD_GRADES[l.child_grade] ?? l.child_grade}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{l.created_at}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${l.signed_up ? "bg-[#DFF5E6] text-[#22874E]" : "bg-gray-100 text-gray-500"}`}>
                      {l.signed_up ? "Signed up" : "Trial only"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ───────────── MAP THEMES ───────────── */
const THEME_KEYS   = ["town", "island", "space", "jungle", "underwater", "candy", "arctic"];
const THEME_LABELS = { town: "🏘️ Town", island: "🏝️ Island", space: "🚀 Space", jungle: "🌴 Jungle", underwater: "🐠 Underwater", candy: "🍭 Candy Land", arctic: "🧊 Arctic" };
const THEME_COLORS = { town: "bg-orange-100 text-orange-700", island: "bg-teal-100 text-teal-700", space: "bg-indigo-100 text-indigo-700", jungle: "bg-[#DFF5E6] text-[#22874E]", underwater: "bg-cyan-100 text-cyan-700", candy: "bg-pink-100 text-pink-700", arctic: "bg-blue-100 text-blue-700" };

const BLANK_THEME = {
  name: "", slug: "", theme_key: "town", description: "", config: null, preview_url: "", is_active: true,
};

function MapThemes({ auth }) {
  const { t: tr } = useTranslation();
  const [themes, setThemes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/kids/map-themes`, auth);
      setThemes(r.data.data || []);
    } catch { setThemes([]); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (t) => {
    try { await axios.put(`${API}/api/edolutions/admin/kids/map-themes/${t.id}/toggle`, {}, auth); load(); }
    catch { toast.error("Couldn't toggle theme."); }
  };

  const setDefault = async (t) => {
    try {
      const r = await axios.put(`${API}/api/edolutions/admin/kids/map-themes/${t.id}/set-default`, {}, auth);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't set default."); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      const r = await axios.delete(`${API}/api/edolutions/admin/kids/map-themes/${t.id}`, auth);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "Couldn't delete theme."); }
  };

  if (loading) return <SkCardGrid count={6} cols="sm:grid-cols-2 lg:grid-cols-3" />;

  return (
    <div>
      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4 mb-5">
        <p className="font-display font-semibold text-navy mb-1">{tr("admin.map.title")}</p>
        <p className="text-sm text-gray-600">
          {tr("admin.map.description")}
          Phase 2 will add a visual editor for building colours and positions. Phase 3 will let AI generate themes from a description.
        </p>
      </div>

      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...BLANK_THEME })}
          className="bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2 rounded-xl text-sm">{tr("admin.map.newTheme")}</button>
      </div>

      {themes.length === 0 && !loading && (
        <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4 mb-4 text-sm text-yellow-800">
          {tr("admin.map.noThemes")}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {themes.map(t => (
          <div key={t.id} className={`bg-white rounded-2xl border shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 flex flex-col ${t.is_active ? "border-[#EEF1F5]" : "border-red-200 opacity-70"} ${t.is_default ? "ring-2 ring-brand" : ""}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold text-navy">{t.name}</div>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${THEME_COLORS[t.theme_key] || "bg-gray-100 text-gray-500"}`}>
                {THEME_LABELS[t.theme_key] || t.theme_key}
              </span>
            </div>
            {t.description && <p className="text-xs text-gray-500 mb-2">{t.description}</p>}
            <div className="flex flex-wrap gap-2 mt-auto pt-3">
              {t.is_default ? (
                <span className="text-xs font-bold px-2 py-1 rounded-full bg-brand text-white">{tr("admin.map.default")}</span>
              ) : (
                <button onClick={() => setDefault(t)}
                  className="text-xs font-semibold px-2 py-1 rounded-lg bg-orange-50 text-brand hover:bg-orange-100">{tr("admin.map.setDefault")}</button>
              )}
              <button onClick={() => toggle(t)}
                className={`text-xs font-semibold px-2 py-1 rounded-full ${t.is_active ? "bg-[#DFF5E6] text-[#22874E]" : "bg-gray-100 text-gray-500"}`}>
                {t.is_active ? tr("admin.common.on") : tr("admin.common.off")}
              </button>
              <button onClick={() => setEditing(t)} className="text-xs font-semibold px-2 py-1 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100">{tr("admin.games.edit")}</button>
              {!t.is_default && <button onClick={() => remove(t)} className="text-xs font-semibold px-2 py-1 rounded-lg bg-red-50 text-red-500 hover:bg-red-100">{tr("admin.games.delete")}</button>}
            </div>
          </div>
        ))}
      </div>

      {editing && <ThemeModal theme={editing} auth={auth} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const THEME_DEFAULTS = {
  town: {
    sky: { stops: [{ offset: 0, color: "#7EC8E3" }, { offset: 0.35, color: "#B5DFF5" }, { offset: 0.6, color: "#D6EFD8" }, { offset: 1, color: "#82C94E" }] },
    ground: { primary: "#72B847", secondary: "#82C94E", accent: "#6DB84A" },
    road: { fill: "#E8D5A8", stroke: "#D0BD88", width: 30, dashArray: "6 18" },
    decorations: { clouds: true, sun: true, mountains: true, trees: true, flowers: true, pond: true, ferrisWheel: true, hotAirBalloon: true, clockTower: true, lamps: true, benches: true, playground: true },
    signpost: { label: "Cabil Town", sublabel: "Learn!" },
  },
  island: {
    sky: { stops: [{ offset: 0, color: "#1CA5C9" }, { offset: 0.4, color: "#67D5E8" }, { offset: 0.65, color: "#A8E6CF" }, { offset: 1, color: "#E8C87A" }] },
    ground: { primary: "#E8C87A", secondary: "#F2D98A", accent: "#6DBF5A" },
    road: { fill: "#F2D98A", stroke: "#E8C87A", width: 28, dashArray: "6 18" },
    decorations: { clouds: true, sun: true, palmTrees: true, treasureChest: true, pirateShip: true },
    signpost: { label: "Pirate Isle", sublabel: "Explore!" },
  },
  space: {
    sky: { stops: [{ offset: 0, color: "#0B0E2A" }, { offset: 0.4, color: "#1A1E4A" }, { offset: 0.7, color: "#2D2670" }, { offset: 1, color: "#4A3C8A" }] },
    ground: { primary: "#2A2650", secondary: "#5A4FA0", accent: "#3D3780" },
    road: { fill: "#5A4FA0", stroke: "#8A7FE0", width: 28, dashArray: "6 18" },
    decorations: { stars: true, planet: true, nebula: true, rocket: true, satellite: true, aliens: true },
    signpost: { label: "Star Base", sublabel: "Blast off!" },
  },
  jungle: {
    sky: { stops: [{ offset: 0, color: "#4A8B3F" }, { offset: 0.3, color: "#6DBF5A" }, { offset: 0.6, color: "#A8D5A0" }, { offset: 1, color: "#2E7D32" }] },
    ground: { primary: "#2E7D32", secondary: "#388E3C", accent: "#1B5E20" },
    road: { fill: "#8B6B3E", stroke: "#6B4F2B", width: 26, dashArray: "4 12" },
    decorations: { vines: true, parrots: true, monkeys: true, waterfall: true, mist: true },
    signpost: { label: "Wild Jungle", sublabel: "Discover!" },
  },
  underwater: {
    sky: { stops: [{ offset: 0, color: "#0A3D62" }, { offset: 0.3, color: "#0E6F9E" }, { offset: 0.6, color: "#1CA5C9" }, { offset: 1, color: "#0E4D64" }] },
    ground: { primary: "#0E4D64", secondary: "#1A6B5A", accent: "#0A3D48" },
    road: { fill: "#1CA5C9", stroke: "#0891B2", width: 26, dashArray: "6 14" },
    decorations: { bubbles: true, lightRays: true, coral: true, seaweed: true, fish: true, whale: true, treasure: true },
    signpost: { label: "Deep Ocean", sublabel: "Dive in!" },
  },
  candy: {
    sky: { stops: [{ offset: 0, color: "#FFB6C1" }, { offset: 0.3, color: "#FFDAB9" }, { offset: 0.6, color: "#FFE4E1" }, { offset: 1, color: "#FFD1DC" }] },
    ground: { primary: "#FFD1DC", secondary: "#FFB6C1", accent: "#FFDAB9" },
    road: { fill: "#FF69B4", stroke: "#FF1493", width: 28, dashArray: "8 12" },
    decorations: { clouds: true, rainbow: true, lollipops: true, candyCanes: true, gummies: true },
    signpost: { label: "Candy Land", sublabel: "Sweet!" },
  },
  arctic: {
    sky: { stops: [{ offset: 0, color: "#1E3A5F" }, { offset: 0.3, color: "#4A90D9" }, { offset: 0.6, color: "#87CEEB" }, { offset: 1, color: "#E0F2FE" }] },
    ground: { primary: "#E0F2FE", secondary: "#BAE6FD", accent: "#D6EFF5" },
    road: { fill: "#BAE6FD", stroke: "#7DD3FC", width: 28, dashArray: "6 18" },
    decorations: { snowflakes: true, aurora: true, icebergs: true, penguins: true, polarbear: true, igloo: true },
    signpost: { label: "Arctic Base", sublabel: "Explore!" },
  },
};
const DECO_LABELS = {
  clouds: "☁️ Clouds", sun: "☀️ Sun", mountains: "⛰️ Mountains", trees: "🌳 Trees",
  flowers: "🌸 Flowers", pond: "🦆 Pond", ferrisWheel: "🎡 Ferris Wheel",
  hotAirBalloon: "🎈 Hot Air Balloon", clockTower: "🕐 Clock Tower",
  lamps: "🔦 Lamps", benches: "🪑 Benches", playground: "🛝 Playground",
  palmTrees: "🌴 Palm Trees", treasureChest: "🏴‍☠️ Treasure", pirateShip: "🚢 Ship",
  stars: "⭐ Stars", planet: "🪐 Planet", nebula: "🌌 Nebula",
  rocket: "🚀 Rocket", satellite: "🛰️ Satellite", aliens: "👾 Aliens",
  vines: "🌿 Vines", parrots: "🦜 Parrots", monkeys: "🐒 Monkeys",
  waterfall: "💧 Waterfall", mist: "🌫️ Mist",
  bubbles: "🫧 Bubbles", lightRays: "☀️ Light Rays", coral: "🪸 Coral",
  seaweed: "🌿 Seaweed", fish: "🐠 Fish", whale: "🐋 Whale", treasure: "💰 Treasure",
  rainbow: "🌈 Rainbow", lollipops: "🍭 Lollipops", candyCanes: "🍬 Candy Canes", gummies: "🧁 Sweets",
  snowflakes: "❄️ Snowflakes", aurora: "🌌 Aurora", icebergs: "🏔️ Icebergs",
  penguins: "🐧 Penguins", polarbear: "🐻‍❄️ Polar Bear", igloo: "🏠 Igloo",
};
const DASH_OPTS = ["6 18", "4 12", "8 8", "10 20", "3 6"];

function ColorPick({ value, onChange, label }) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <input type="color" value={value || "#000000"} onChange={e => onChange(e.target.value)}
        className="w-7 h-7 rounded-lg border border-gray-200 cursor-pointer p-0" />
      <span className="text-gray-600">{label}</span>
      <span className="font-mono text-gray-400 text-[10px]">{value}</span>
    </label>
  );
}

function ThemeMiniPreview({ config, themeKey }) {
  const d = config;
  const skyStops = d?.sky?.stops || THEME_DEFAULTS[themeKey]?.sky?.stops || [];
  const gnd = d?.ground || THEME_DEFAULTS[themeKey]?.ground || {};
  const rd = d?.road || THEME_DEFAULTS[themeKey]?.road || {};
  const blds = d?.buildings || {};
  const defaultColors = ["#EE5F2B", "#2FA15A", "#13B6C9", "#7A4FD0", "#E0518A", "#C8491E", "#EE5F2B"];

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-[#F7F8FA]">
      <svg viewBox="0 0 300 180" width="100%" style={{ display: "block" }}>
        <defs>
          <linearGradient id="prevSky" x1="0" y1="0" x2="0" y2="1">
            {skyStops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
          </linearGradient>
        </defs>
        <rect width="300" height="180" fill="url(#prevSky)" />
        <rect x="0" y="120" width="300" height="60" fill={gnd.primary || "#72B847"} />
        <ellipse cx="150" cy="120" rx="180" ry="15" fill={gnd.secondary || "#82C94E"} />
        <polyline points="50,145 110,125 190,140 250,120" fill="none" stroke={rd.fill || "#E8D5A8"} strokeWidth={Math.min(rd.width || 30, 20)} strokeLinejoin="round" strokeLinecap="round" />
        <polyline points="50,145 110,125 190,140 250,120" fill="none" stroke={rd.stroke || "#D0BD88"} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" strokeDasharray={rd.dashArray || "4 8"} />
        {[{ x: 40, y: 130 }, { x: 100, y: 112 }, { x: 180, y: 126 }, { x: 240, y: 107 }].map((b, i) => {
          const c = blds[(i + 1).toString()]?.color || defaultColors[i];
          return (
            <g key={i}>
              <rect x={b.x} y={b.y - 22} width="20" height="22" rx="2" fill={c} />
              <polygon points={`${b.x - 2},${b.y - 22} ${b.x + 10},${b.y - 30} ${b.x + 22},${b.y - 22}`} fill={c} opacity=".7" />
              <rect x={b.x + 3} y={b.y - 18} width="5" height="4" rx="1" fill="rgba(255,255,255,.5)" />
              <rect x={b.x + 12} y={b.y - 18} width="5" height="4" rx="1" fill="rgba(255,255,255,.5)" />
            </g>
          );
        })}
        <circle cx="270" cy="20" r="14" fill="#FFE066" opacity=".8" />
        <circle cx="270" cy="20" r="10" fill="#FFD700" />
      </svg>
      <p className="text-center text-[10px] text-gray-400 py-1">Approximate preview</p>
    </div>
  );
}

function ThemeModal({ theme, auth, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...theme });
  const [saving, setSaving] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [aiDesc, setAiDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [svgDesc, setSvgDesc] = useState("");
  const [svgBusy, setSvgBusy] = useState(false);
  const [generatedSvg, setGeneratedSvg] = useState(null);
  const [showSvgCode, setShowSvgCode] = useState(false);
  const isNew = !theme.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const defaults = THEME_DEFAULTS[form.theme_key] || THEME_DEFAULTS.town;
  const [config, setConfig] = useState(() => {
    const base = JSON.parse(JSON.stringify(defaults));
    if (theme.config) {
      Object.keys(theme.config).forEach(k => {
        if (typeof theme.config[k] === 'object' && !Array.isArray(theme.config[k]) && base[k]) {
          base[k] = { ...base[k], ...theme.config[k] };
        } else if (theme.config[k] !== undefined) {
          base[k] = theme.config[k];
        }
      });
    }
    return base;
  });

  const setC = (path, val) => {
    setConfig(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, config: { version: 1, ...config } };
      if (isNew) await axios.post(`${API}/api/edolutions/admin/kids/map-themes`, payload, auth);
      else       await axios.put(`${API}/api/edolutions/admin/kids/map-themes/${theme.id}`, payload, auth);
      toast.success(isNew ? "Theme created." : "Theme updated.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save theme.");
    } finally { setSaving(false); }
  };

  const generateAI = async () => {
    if (!aiDesc.trim()) return toast.warn("Describe the theme first.");
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/api/edolutions/admin/kids/map-themes/generate`,
        { description: aiDesc, theme_key: form.theme_key }, auth);
      const result = r.data?.data;
      if (result?.config) {
        setConfig(prev => {
          const merged = JSON.parse(JSON.stringify(prev));
          Object.keys(result.config).forEach(k => {
            if (k === 'version') return;
            if (typeof result.config[k] === 'object' && !Array.isArray(result.config[k]) && merged[k]) {
              merged[k] = { ...merged[k], ...result.config[k] };
            } else if (result.config[k] !== undefined) {
              merged[k] = result.config[k];
            }
          });
          return merged;
        });
        const aiU = result?.ai_usage;
        const uMsg = aiU ? ` (${aiU.input_tokens + aiU.output_tokens} tokens · ~$${aiU.cost_estimate})` : "";
        toast.success(`AI theme applied!${uMsg} Fine-tune below.`);
      }
      if (result?.suggested_name && !form.name) set("name", result.suggested_name);
      if (result?.suggested_theme_key && result.suggested_theme_key !== form.theme_key) {
        if (window.confirm(`AI suggests "${result.suggested_theme_key}" scene type. Switch?`)) {
          set("theme_key", result.suggested_theme_key);
        }
      }
    } catch (e) { toast.error(e.response?.data?.message || "AI generation failed."); }
    finally { setAiBusy(false); }
  };

  const generateSvg = async () => {
    if (!svgDesc.trim()) return toast.warn("Describe what SVG to create.");
    setSvgBusy(true);
    try {
      const r = await axios.post(`${API}/api/edolutions/admin/kids/map-themes/generate-svg`,
        { description: svgDesc, theme_key: form.theme_key }, auth);
      const result = r.data?.data;
      if (result?.svg) {
        setGeneratedSvg(result);
        const aiU = result.ai_usage;
        const uMsg = aiU ? ` (${aiU.input_tokens + aiU.output_tokens} tokens · ~$${aiU.cost_estimate})` : "";
        toast.success(`SVG created!${uMsg}`);
      } else { toast.error("No SVG returned. Try again."); }
    } catch (e) { toast.error(e.response?.data?.message || "SVG generation failed."); }
    finally { setSvgBusy(false); }
  };

  const decoKeys = Object.keys(defaults.decorations || {});

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-semibold text-lg text-navy mb-4">{isNew ? "New theme" : `Edit: ${theme.name}`}</h2>

        <div className="grid lg:grid-cols-5 gap-5">
          {/* ── Left: Editor ── */}
          <div className="lg:col-span-3 space-y-4">
            {/* Basic fields */}
            <div className="grid sm:grid-cols-2 gap-3">
              <L label="Name"><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} /></L>
              <L label="Slug (optional)"><input className={inp} value={form.slug || ""} onChange={e => set("slug", e.target.value)} /></L>
              <L label="Theme type"><Sel value={form.theme_key} opts={THEME_KEYS} onChange={v => { set("theme_key", v); setConfig(JSON.parse(JSON.stringify(THEME_DEFAULTS[v] || THEME_DEFAULTS.town))); }} /></L>
              <L label="Description"><input className={inp} value={form.description || ""} onChange={e => set("description", e.target.value)} placeholder="Short description…" /></L>
            </div>

            {/* AI Generator */}
            <div className="rounded-xl bg-[#F6F2FF] border border-[#E4DAFB] p-3">
              <p className="font-display font-semibold text-sm text-navy mb-1">✨ Generate with AI</p>
              <p className="text-xs text-[#8A80B0] mb-2">Describe a world and AI will design the colours and decorations.</p>
              <div className="flex gap-2">
                <input className="flex-1 px-3 py-2 rounded-xl border border-[#E0D6F5] bg-white outline-none focus:border-aiaccent text-sm" value={aiDesc} onChange={e => setAiDesc(e.target.value)}
                  placeholder="e.g. A magical underwater coral reef world…"
                  onKeyDown={e => e.key === "Enter" && generateAI()} />
                <button onClick={generateAI} disabled={aiBusy}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-aiaccent hover:opacity-90 disabled:opacity-50 text-white whitespace-nowrap">
                  {aiBusy ? "Generating…" : "✨ Generate"}
                </button>
              </div>
            </div>

            {/* AI SVG Decoration Generator */}
            <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3">
              <p className="font-display font-semibold text-sm text-navy mb-1">🎨 Generate SVG Decoration with AI</p>
              <p className="text-xs text-gray-500 mb-2">Describe any object and AI will create a cute cartoon SVG for your theme.</p>
              <div className="flex gap-2">
                <input className={`${inp} flex-1`} value={svgDesc || ""} onChange={e => setSvgDesc(e.target.value)}
                  placeholder="e.g. a cute mushroom house, a rainbow bridge, a crystal tree..."
                  onKeyDown={e => e.key === "Enter" && generateSvg()} />
                <button onClick={generateSvg} disabled={svgBusy}
                  className="px-3 py-2 rounded-xl text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white whitespace-nowrap">
                  {svgBusy ? "Creating..." : "🎨 Create SVG"}
                </button>
              </div>
              {generatedSvg && (
                <div className="mt-3 flex items-start gap-3 bg-white rounded-lg p-3 border border-indigo-100">
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg bg-[#F7F8FA] border flex items-center justify-center overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: generatedSvg.svg?.replace(/<svg/, '<svg width="80" height="80"') }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-navy">{generatedSvg.name}</p>
                    <p className="text-xs text-gray-500">{generatedSvg.description}</p>
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => { navigator.clipboard.writeText(generatedSvg.svg); toast.success("SVG copied!"); }}
                        className="text-[10px] font-bold px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg">Copy SVG</button>
                      <button onClick={() => { setShowSvgCode(!showSvgCode); }}
                        className="text-[10px] font-bold px-2 py-1 bg-gray-100 text-gray-600 rounded-lg">{showSvgCode ? "Hide" : "View"} Code</button>
                    </div>
                    {showSvgCode && (
                      <pre className="mt-2 p-2 bg-[#F7F8FA] rounded text-[9px] font-mono text-gray-500 max-h-24 overflow-auto border">{generatedSvg.svg}</pre>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Sky Gradient */}
            <details open className="rounded-xl border border-[#EEF1F5] p-3">
              <summary className="font-semibold text-sm text-navy cursor-pointer">🌤️ Sky Gradient</summary>
              <div className="mt-2 space-y-2">
                {(config.sky?.stops || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <ColorPick value={s.color} onChange={c => { const stops = [...config.sky.stops]; stops[i] = { ...stops[i], color: c }; setC("sky.stops", stops); }} label={`Stop ${i + 1} (${Math.round(s.offset * 100)}%)`} />
                  </div>
                ))}
              </div>
            </details>

            {/* Ground */}
            <details className="rounded-xl border border-[#EEF1F5] p-3">
              <summary className="font-semibold text-sm text-navy cursor-pointer">🟩 Ground</summary>
              <div className="mt-2 flex flex-wrap gap-4">
                <ColorPick value={config.ground?.primary} onChange={c => setC("ground.primary", c)} label="Primary" />
                <ColorPick value={config.ground?.secondary} onChange={c => setC("ground.secondary", c)} label="Secondary" />
                <ColorPick value={config.ground?.accent} onChange={c => setC("ground.accent", c)} label="Accent" />
              </div>
            </details>

            {/* Road */}
            <details className="rounded-xl border border-[#EEF1F5] p-3">
              <summary className="font-semibold text-sm text-navy cursor-pointer">🛣️ Road</summary>
              <div className="mt-2 flex flex-wrap gap-4 items-end">
                <ColorPick value={config.road?.fill} onChange={c => setC("road.fill", c)} label="Fill" />
                <ColorPick value={config.road?.stroke} onChange={c => setC("road.stroke", c)} label="Dash" />
                <L label="Width">
                  <input type="range" min="10" max="50" value={config.road?.width || 30}
                    onChange={e => setC("road.width", parseInt(e.target.value))} className="w-24" />
                  <span className="text-xs text-gray-400 ms-1">{config.road?.width || 30}px</span>
                </L>
                <L label="Dash Pattern">
                  <select value={config.road?.dashArray || "6 18"} onChange={e => setC("road.dashArray", e.target.value)}
                    className="px-2 py-1 rounded-lg border border-gray-200 text-xs">
                    {DASH_OPTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </L>
              </div>
            </details>

            {/* Buildings */}
            <details className="rounded-xl border border-[#EEF1F5] p-3">
              <summary className="font-semibold text-sm text-navy cursor-pointer">🏘️ Building Colours (1–7)</summary>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[1,2,3,4,5,6,7].map(i => (
                  <ColorPick key={i}
                    value={config.buildings?.[(i).toString()]?.color || ["#EE5F2B","#2FA15A","#13B6C9","#7A4FD0","#E0518A","#C8491E","#EE5F2B"][i-1]}
                    onChange={c => setC(`buildings.${i}.color`, c)}
                    label={`Stop ${i}`} />
                ))}
              </div>
            </details>

            {/* Decorations */}
            <details className="rounded-xl border border-[#EEF1F5] p-3">
              <summary className="font-semibold text-sm text-navy cursor-pointer">🎨 Decorations</summary>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {decoKeys.map(dk => (
                  <label key={dk} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox"
                      checked={config.decorations?.[dk]?.visible !== undefined ? config.decorations[dk].visible : (config.decorations?.[dk] !== false)}
                      onChange={e => setC(`decorations.${dk}`, { visible: e.target.checked })} />
                    {DECO_LABELS[dk] || dk}
                  </label>
                ))}
              </div>
            </details>

            {/* Signpost */}
            <details className="rounded-xl border border-[#EEF1F5] p-3">
              <summary className="font-semibold text-sm text-navy cursor-pointer">🪧 Signpost</summary>
              <div className="mt-2 grid sm:grid-cols-2 gap-3">
                <L label="Label (max 15)"><input className={inp} maxLength={15} value={config.signpost?.label || ""} onChange={e => setC("signpost.label", e.target.value)} /></L>
                <L label="Sublabel (max 12)"><input className={inp} maxLength={12} value={config.signpost?.sublabel || ""} onChange={e => setC("signpost.sublabel", e.target.value)} /></L>
              </div>
            </details>

            {/* Raw JSON toggle */}
            <div>
              <button onClick={() => setShowJson(!showJson)} className="text-xs text-gray-400 hover:text-gray-600">
                {showJson ? "Hide" : "Show"} raw JSON
              </button>
              {showJson && (
                <pre className="mt-1 p-2 bg-[#F7F8FA] rounded-lg text-[10px] font-mono text-gray-500 max-h-40 overflow-auto border">
                  {JSON.stringify({ version: 1, ...config }, null, 2)}
                </pre>
              )}
            </div>
          </div>

          {/* ── Right: Preview ── */}
          <div className="lg:col-span-2 space-y-3">
            <div className="sticky top-0">
              <p className="text-sm font-semibold text-navy mb-2">Live Preview</p>
              <ThemeMiniPreview config={config} themeKey={form.theme_key} />

              <label className="flex items-center gap-2 mt-4 text-sm">
                <input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} />
                Active (available for selection)
              </label>

              <button onClick={() => { setConfig(JSON.parse(JSON.stringify(defaults))); toast.info("Reset to defaults."); }}
                className="mt-2 text-xs text-gray-400 hover:text-red-500">
                Reset to defaults
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 border-t pt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100">{t("admin.common.cancel")}</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl text-sm font-semibold bg-brand hover:bg-brand-hover disabled:opacity-50 text-white">
            {saving ? t("admin.map.saving") : t("admin.map.saveTheme")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────── EMAIL BROADCAST ───────────── */
const AUDIENCES = [
  ["all",         "All parents"],
  ["active",      "Active parents"],
  ["inactive",    "Inactive parents"],
  ["unverified",  "Unverified email"],
  ["no_children", "No children added"],
  ["custom",      "Custom emails"],
];

const TEMPLATE_OPTIONS = [
  ["broadcast",     "Custom Broadcast"],
  ["new_features",  "New Features Launch"],
  ["welcome",       "CEO Welcome"],
  ["onboarding",    "Onboarding Nudge"],
];

function EmailBroadcast({ auth }) {
  const { t } = useTranslation();
  const [stats, setStats]     = useState(null);
  const [sending, setSending] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [result, setResult]   = useState(null);

  const [form, setForm] = useState({
    template: "broadcast", audience: "active", subject: "",
    body: "", sender_name: "Amara Hashmi", sender_role: "CEO & Founder, Cabil",
    cta_url: "https://cabil.co/kids/login", cta_label: "Go to Cabil", custom_emails: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/api/edolutions/admin/kids/email/stats`, auth)
      .then(r => setStats(r.data.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendEmail = async () => {
    if (!form.subject.trim()) return toast.warn("Subject is required.");
    if (form.template === "broadcast" && !form.body.trim()) return toast.warn("Message body is required.");
    setSending(true); setResult(null);
    try {
      const payload = { ...form };
      if (form.audience === "custom") {
        payload.custom_emails = form.custom_emails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean);
      }
      const r = await axios.post(`${API}/api/edolutions/admin/kids/email/send`, payload, auth);
      setResult(r.data.data);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.message || "Failed to send."); }
    finally { setSending(false); }
  };

  const sendNudge = async () => {
    if (!window.confirm("Send onboarding nudge to all unverified / no-children users?")) return;
    setNudging(true);
    try {
      const r = await axios.post(`${API}/api/edolutions/admin/kids/email/onboarding-nudge`, {}, auth);
      toast.success(r.data.message);
      setResult(r.data.data);
    } catch (e) { toast.error(e.response?.data?.message || "Failed."); }
    finally { setNudging(false); }
  };

  return (
    <div>
      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4 mb-5">
        <p className="font-display font-semibold text-navy mb-1">{t("admin.email.title")}</p>
        <p className="text-sm text-gray-600">
          {t("admin.email.description")} Choose a template, select the audience, compose your message, and hit Send.
        </p>
      </div>

      {/* Quick stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {[["All", stats.all], ["Active", stats.active], ["Inactive", stats.inactive], ["Unverified", stats.unverified], ["No children", stats.no_children]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-xl border border-[#EEF1F5] p-3 text-center">
              <div className="text-xl font-extrabold text-navy">{v}</div>
              <div className="text-xs text-gray-500">{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Quick action: onboarding nudge */}
      <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-display font-semibold text-sm text-navy">{t("admin.email.nudgeTitle")}</p>
          <p className="text-xs text-gray-500">{t("admin.email.nudgeDesc")}</p>
        </div>
        <button onClick={sendNudge} disabled={nudging}
          className="bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-xl text-sm whitespace-nowrap">
          {nudging ? t("admin.email.sending") : t("admin.email.sendNudge")}
        </button>
      </div>

      {/* Compose form */}
      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-6">
        <h3 className="font-display font-semibold text-navy mb-4">{t("admin.email.compose")}</h3>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.template")}</label>
            <select value={form.template} onChange={e => set("template", e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm">
              {TEMPLATE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.audience")}</label>
            <select value={form.audience} onChange={e => set("audience", e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm">
              {AUDIENCES.map(([k, l]) => <option key={k} value={k}>{l} {stats ? `(${stats[k] || "?"})` : ""}</option>)}
            </select>
          </div>
        </div>

        {form.audience === "custom" && (
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-600 mb-1">Email addresses (comma or newline separated)</label>
            <textarea rows={3} value={form.custom_emails} onChange={e => set("custom_emails", e.target.value)}
              placeholder="parent1@example.com, parent2@example.com"
              className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.subject")}</label>
          <input value={form.subject} onChange={e => set("subject", e.target.value)}
            placeholder="e.g. Exciting new features on Cabil!"
            className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
        </div>

        {form.template === "broadcast" && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.body")}</label>
              <textarea rows={6} value={form.body} onChange={e => set("body", e.target.value)}
                placeholder="Write your message here. Use plain text — line breaks will be preserved."
                className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.senderName")}</label>
                <input value={form.sender_name} onChange={e => set("sender_name", e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.senderRole")}</label>
                <input value={form.sender_role} onChange={e => set("sender_role", e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.ctaUrl")}</label>
                <input value={form.cta_url} onChange={e => set("cta_url", e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">{t("admin.email.ctaLabel")}</label>
                <input value={form.cta_label} onChange={e => set("cta_label", e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
              </div>
            </div>
          </>
        )}

        {result && (
          <div className="rounded-xl bg-green-50 border border-green-200 p-3 mb-4 text-sm text-green-800">
            ✅ Sent: <b>{result.sent}</b> · Failed: {result.failed} · Total: {result.total}
          </div>
        )}

        <button onClick={sendEmail} disabled={sending}
          className="bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-semibold px-6 py-2.5 rounded-xl text-sm">
          {sending ? t("admin.email.sending") : t("admin.email.sendEmail")}
        </button>
      </div>
    </div>
  );
}

/* ───────────── BLOG ADMIN ───────────── */
const BLANK_POST = {
  title: "", slug: "", category: "", status: "draft", excerpt: "", content: "",
  image_url: "", meta_title: "", meta_description: "",
};

const BLOG_STATUSES = ["draft", "published"];

function BlogAdmin({ auth }) {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/edolutions/admin/blog`, auth);
      setPosts(r.data.data || []);
    } catch { toast.error("Failed to load blog posts."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      const r = await axios.delete(`${API}/api/edolutions/admin/blog/${p.id}`, auth);
      toast.success(r.data.message || "Post deleted.");
      load();
    } catch { toast.error("Couldn't delete post."); }
  };

  if (loading) return <SkTable cols={6} rows={6} />;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...BLANK_POST })}
          className="bg-brand hover:bg-brand-hover text-white font-semibold px-4 py-2 rounded-xl text-sm">+ New Post</button>
      </div>

      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F8FA] text-gray-500 text-start">
            <tr>
              <th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Views</th>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-gray-400">No blog posts yet.</td></tr>
            ) : posts.map(p => (
              <tr key={p.id} className="border-t border-[#EEF1F5]">
                <td className="px-4 py-3 font-semibold text-gray-800">
                  {p.title}
                  <div className="text-xs text-gray-400 font-normal">{p.slug}</div>
                </td>
                <td className="px-4 py-3 capitalize">{p.category || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    p.status === "published" ? "bg-[#DFF5E6] text-[#22874E]" : "bg-yellow-100 text-yellow-700"
                  }`}>
                    {p.status === "published" ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{p.views ?? 0}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{p.created_at || "—"}</td>
                <td className="px-4 py-3 text-end whitespace-nowrap">
                  <button onClick={() => setEditing(p)} className="text-navy hover:underline me-3">Edit</button>
                  <button onClick={() => remove(p)} className="text-red-500 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <BlogModal post={editing} auth={auth} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BlogModal({ post, auth, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK_POST, ...post });
  const [saving, setSaving] = useState(false);
  const isNew = !post.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.warn("Title is required.");
    setSaving(true);
    try {
      if (isNew) await axios.post(`${API}/api/edolutions/admin/blog`, form, auth);
      else       await axios.put(`${API}/api/edolutions/admin/blog/${post.id}`, form, auth);
      toast.success(isNew ? "Post created." : "Post updated.");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || "Couldn't save post.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-semibold text-lg text-navy mb-4">{isNew ? "New Post" : `Edit: ${post.title}`}</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Title"><input className={inp} value={form.title} onChange={e => set("title", e.target.value)} /></L>
          <L label="Slug (optional — auto if blank)"><input className={inp} value={form.slug || ""} onChange={e => set("slug", e.target.value)} /></L>
          <L label="Category"><input className={inp} value={form.category || ""} onChange={e => set("category", e.target.value)} placeholder="e.g. education, parenting, tips" /></L>
          <L label="Status">
            <select value={form.status} onChange={e => set("status", e.target.value)} className={inp}>
              {BLOG_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </L>
          <L label="Image URL"><input className={inp} value={form.image_url || ""} onChange={e => set("image_url", e.target.value)} placeholder="https://..." /></L>
        </div>

        <div className="mt-4">
          <L label="Excerpt">
            <textarea rows={2} value={form.excerpt || ""} onChange={e => set("excerpt", e.target.value)}
              placeholder="Short summary of the post…"
              className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
          </L>
        </div>

        <div className="mt-4">
          <L label="Content">
            <textarea rows={8} value={form.content || ""} onChange={e => set("content", e.target.value)}
              placeholder="Full blog post content…"
              className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />
          </L>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <L label="Meta Title (SEO)"><input className={inp} value={form.meta_title || ""} onChange={e => set("meta_title", e.target.value)} /></L>
          <L label="Meta Description (SEO)"><input className={inp} value={form.meta_description || ""} onChange={e => set("meta_description", e.target.value)} /></L>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl text-sm font-semibold bg-brand hover:bg-brand-hover disabled:opacity-50 text-white">
            {saving ? "Saving…" : "Save Post"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────── LANGUAGE EDITOR ───────────── */
function flattenObj(obj, prefix = "") {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) Object.assign(out, flattenObj(v, key));
    else out[key] = v;
  }
  return out;
}

function nestObj(flat) {
  const out = {};
  for (const [key, val] of Object.entries(flat)) {
    const parts = key.split(".");
    let cur = out;
    parts.forEach((p, i) => {
      if (i === parts.length - 1) cur[p] = val;
      else { if (!cur[p] || typeof cur[p] !== "object") cur[p] = {}; cur = cur[p]; }
    });
  }
  return out;
}

function LanguageEditor() {
  const { i18n } = useTranslation();
  const [enFlat] = useState(() => flattenObj(enStrings));
  const [arEdits, setArEdits] = useState(() => flattenObj(arStrings));
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("all");
  const [dirty, setDirty] = useState(false);

  const sections = [...new Set(Object.keys(enFlat).map(k => k.split(".")[0]))];

  const filtered = Object.keys(enFlat).filter(k => {
    if (section !== "all" && !k.startsWith(section + ".")) return false;
    if (search) {
      const s = search.toLowerCase();
      return k.toLowerCase().includes(s) || (enFlat[k] || "").toLowerCase().includes(s) || (arEdits[k] || "").toLowerCase().includes(s);
    }
    return true;
  });

  const updateAr = (key, val) => {
    setArEdits(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const apply = () => {
    const nested = nestObj(arEdits);
    i18n.addResourceBundle("ar", "translation", nested, true, true);
    toast.success(`Applied ${Object.keys(arEdits).length} Arabic translations to the live app.`);
    setDirty(false);
  };

  const exportJson = () => {
    const nested = nestObj(arEdits);
    const blob = new Blob([JSON.stringify(nested, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "ar.json"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Arabic translations exported as ar.json");
  };

  const missingCount = Object.keys(enFlat).filter(k => !arEdits[k]?.trim()).length;

  return (
    <div>
      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4 mb-5">
        <p className="font-display font-semibold text-navy mb-1">🌐 Language Editor</p>
        <p className="text-sm text-gray-600">
          Edit Arabic translations for the entire site. Changes apply live when you click <b>Apply</b>.
          Use <b>Export</b> to download the updated ar.json for permanent deployment.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search keys or text…"
          className="flex-1 min-w-[200px] max-w-md px-4 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm" />

        <select value={section} onChange={e => setSection(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand">
          <option value="all">All sections ({Object.keys(enFlat).length})</option>
          {sections.map(s => (
            <option key={s} value={s}>{s} ({Object.keys(enFlat).filter(k => k.startsWith(s + ".")).length})</option>
          ))}
        </select>

        {missingCount > 0 && (
          <span className="text-xs font-bold px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
            {missingCount} missing
          </span>
        )}

        <button onClick={apply} disabled={!dirty}
          className="bg-brand hover:bg-brand-hover disabled:opacity-40 text-white font-semibold px-4 py-2 rounded-xl text-sm">
          Apply Live
        </button>
        <button onClick={exportJson}
          className="bg-navy hover:bg-slate-700 text-white font-semibold px-4 py-2 rounded-xl text-sm">
          Export ar.json
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F8FA] text-gray-500 text-start">
            <tr>
              <th className="px-4 py-3 w-[28%]">Key</th>
              <th className="px-4 py-3 w-[36%]">English</th>
              <th className="px-4 py-3 w-[36%]">العربية (Arabic)</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 100).map(k => (
              <tr key={k} className="border-t border-[#EEF1F5] align-top">
                <td className="px-4 py-2 font-mono text-xs text-gray-400 break-all">{k}</td>
                <td className="px-4 py-2 text-gray-700">{enFlat[k]}</td>
                <td className="px-4 py-2">
                  <input value={arEdits[k] || ""} onChange={e => updateAr(k, e.target.value)} dir="rtl"
                    className={`w-full px-2 py-1 rounded-lg border text-sm outline-none focus:border-brand ${arEdits[k]?.trim() ? "border-gray-200" : "border-yellow-300 bg-yellow-50"}`}
                    placeholder="ترجمة…" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 100 && (
          <div className="text-center py-3 text-xs text-gray-400">
            Showing 100 of {filtered.length} — use search or section filter to narrow down.
          </div>
        )}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-gray-400">No matches found.</div>
        )}
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2 rounded-xl border border-gray-200 outline-none focus:border-brand text-sm";
function L({ label, children }) {
  return <div><label className="block text-sm font-semibold text-gray-600 mb-1">{label}</label>{children}</div>;
}
function Sel({ value, opts, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className={inp}>
      {opts.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function PlatformReports({ auth }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom]       = useState("");
  const [to, setTo]           = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [grade, setGrade]     = useState("");
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/edolutions/admin/kids/schools`, auth)
      .then(r => setSchools(r.data.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.append("from", from);
      if (to) params.append("to", to);
      if (schoolId) params.append("school_id", schoolId);
      if (grade !== "") params.append("grade", grade);
      const r = await axios.get(`${API}/api/edolutions/admin/kids/reports?${params}`, auth);
      setData(r.data.data);
    } catch { toast.error("Failed to load report."); }
    finally { setLoading(false); }
  }, [from, to, schoolId, grade]);

  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    if (!data?.student_performance) return;
    const rows = [["Name", "Grade", "School ID", "Parent Email", "Level", "Total XP", "Streak", "Games", "XP (period)", "Time (min)"]];
    data.student_performance.forEach(s => rows.push([s.name, s.grade_label, s.school_id || "N/A", s.parent_email || "", s.level, s.total_xp, s.streak, s.games_completed, s.xp_earned, s.time_mins]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `platform-report-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    toast.success("CSV downloaded!");
  };

  const subjectColors = { math: "#3B82F6", english: "#10B981", science: "#8B5CF6", arabic: "#F59E0B", art: "#EC4899", coding: "#6366F1", general: "#6B7280" };

  return (
    <div className="space-y-5 print:space-y-3">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 print:hidden">
        <h3 className="font-display font-semibold text-navy mb-3">Filters</h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inp} /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inp} /></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">School</label>
            <select value={schoolId} onChange={e => setSchoolId(e.target.value)} className={inp}>
              <option value="">All Schools</option>
              {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-gray-500 mb-1">Grade</label>
            <select value={grade} onChange={e => setGrade(e.target.value)} className={inp}>
              <option value="">All Grades</option><option value="0">KG</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select></div>
          <button onClick={load} className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-navy text-white">Apply</button>
          <button onClick={() => { setFrom(""); setTo(""); setSchoolId(""); setGrade(""); }} className="px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100">Reset</button>
          <div className="flex-1" />
          <button onClick={exportCSV} className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-green-600 text-white">📥 CSV</button>
          <button onClick={() => window.print()} className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white">🖨 PDF</button>
        </div>
      </div>

      {loading && (
        <>
          <SkStatGrid count={10} cols="grid-cols-2 md:grid-cols-3 lg:grid-cols-5" />
          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 space-y-3">
              <Skeleton className="h-4 w-40" rounded="6px" />
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" rounded="999px" />)}
            </div>
            <SkTable cols={4} rows={5} />
          </div>
          <div className="mt-5"><SkTable cols={3} rows={4} /></div>
          <div className="mt-5"><SkTable cols={8} rows={6} /></div>
        </>
      )}
      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              ["🧒", "Students", data.summary.total_students],
              ["👪", "Parents", data.summary.total_parents],
              ["🏫", "Schools", data.summary.total_schools],
              ["🎮", "Completed", data.summary.games_completed],
              ["🔄", "Attempts", data.summary.total_attempts],
              ["⭐", "XP", data.summary.total_xp],
              ["⏱", "Time (min)", data.summary.total_time_mins],
              ["📊", "Avg Score", `${data.summary.avg_score}%`],
              ["🤖", "AI Msgs", data.summary.ai_messages_used],
              ["💳", "Subs", Object.values(data.summary.subscriptions || {}).reduce((a, b) => a + b, 0)],
            ].map(([icon, label, val]) => (
              <div key={label} className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-4">
                <div className="text-lg mb-1">{icon}</div>
                <div className="text-xl font-extrabold text-navy">{val}</div>
                <div className="text-[10px] text-gray-400">{label}</div>
              </div>
            ))}
          </div>

          {/* Subscription breakdown */}
          {data.summary.subscriptions && Object.keys(data.summary.subscriptions).length > 0 && (
            <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5">
              <h3 className="font-display font-semibold text-navy mb-3">💳 Subscription Status</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(data.summary.subscriptions).map(([status, cnt]) => (
                  <div key={status} className={`px-4 py-2 rounded-xl text-sm font-semibold ${
                    status === "active" ? "bg-[#DFF5E6] text-[#22874E]" :
                    status === "trialing" ? "bg-blue-100 text-blue-700" :
                    status === "cancelled" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
                  }`}>{status}: {cnt}</div>
                ))}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5">
            {/* Grade Distribution */}
            <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5">
              <h3 className="font-display font-semibold text-navy mb-3">📚 Grade Distribution</h3>
              {data.grade_distribution && Object.keys(data.grade_distribution).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(data.grade_distribution).map(([g, cnt]) => {
                    const max = Math.max(...Object.values(data.grade_distribution));
                    return (
                      <div key={g} className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-600 w-8">{g}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-5">
                          <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full flex items-center justify-end pe-2"
                            style={{ width: `${Math.max((cnt / max) * 100, 10)}%` }}>
                            <span className="text-[10px] font-bold text-white">{cnt}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <Empty text="No data" />}
            </div>

            {/* Subject Breakdown */}
            <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5">
              <h3 className="font-display font-semibold text-navy mb-3">🎯 Subject Breakdown</h3>
              {data.subject_breakdown?.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-400 uppercase"><tr>
                    <th className="text-start pb-2">Subject</th><th className="text-end pb-2">Plays</th>
                    <th className="text-end pb-2">XP</th><th className="text-end pb-2">Avg</th>
                  </tr></thead>
                  <tbody>
                    {data.subject_breakdown.map(s => (
                      <tr key={s.subject} className="border-t border-gray-50">
                        <td className="py-2 capitalize flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: subjectColors[s.subject] || "#999" }} />{s.subject}
                        </td>
                        <td className="py-2 text-end font-semibold">{s.plays}</td>
                        <td className="py-2 text-end">{parseInt(s.xp)}</td>
                        <td className="py-2 text-end">{Math.round(s.avg_score)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <Empty text="No data" />}
            </div>
          </div>

          {/* School Stats */}
          {data.school_stats?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5">
              <h3 className="font-display font-semibold text-navy mb-3">🏫 Schools by Student Count</h3>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-400 uppercase"><tr>
                  <th className="text-start pb-2">School</th><th className="text-end pb-2">Students</th><th className="text-end pb-2">Plan</th>
                </tr></thead>
                <tbody>
                  {data.school_stats.map(s => (
                    <tr key={s.id} className="border-t border-gray-50">
                      <td className="py-2 font-semibold">{s.name}</td>
                      <td className="py-2 text-end">{s.students}</td>
                      <td className="py-2 text-end capitalize text-gray-500">{s.plan}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Student Performance */}
          <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5">
            <h3 className="font-display font-semibold text-navy mb-3">🧒 Top Student Performance</h3>
            {data.student_performance?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-400 uppercase"><tr>
                    <th className="text-start pb-2">Student</th><th className="text-start pb-2">Grade</th>
                    <th className="text-start pb-2">Parent</th><th className="text-end pb-2">Lv</th>
                    <th className="text-end pb-2">XP</th><th className="text-end pb-2">Streak</th>
                    <th className="text-end pb-2">Games</th><th className="text-end pb-2">Time</th>
                  </tr></thead>
                  <tbody>
                    {data.student_performance.map(s => (
                      <tr key={s.id} className="border-t border-gray-50">
                        <td className="py-2 font-semibold">{s.name}</td>
                        <td className="py-2 text-gray-500">{s.grade_label}</td>
                        <td className="py-2 text-xs text-gray-400">{s.parent_email || "—"}</td>
                        <td className="py-2 text-end">{s.level}</td>
                        <td className="py-2 text-end font-semibold text-navy">{s.total_xp}</td>
                        <td className="py-2 text-end">{s.streak > 0 ? `🔥${s.streak}` : "—"}</td>
                        <td className="py-2 text-end">{s.games_completed}</td>
                        <td className="py-2 text-end text-gray-400">{s.time_mins}m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty text="No students found" />}
          </div>
        </>
      )}
    </div>
  );
}

/* ───────────── LOADING SKELETONS ───────────── */
function SkStatGrid({ count = 8, cols = "sm:grid-cols-2 lg:grid-cols-4" }) {
  return (
    <div className={`grid ${cols} gap-4`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col items-center gap-2 rounded-card bg-white border border-cardline shadow-card px-4 py-4">
          <Skeleton className="w-6 h-6" rounded="8px" />
          <Skeleton className="w-12 h-6" rounded="6px" />
          <Skeleton className="w-16 h-3" rounded="4px" />
        </div>
      ))}
    </div>
  );
}

function SkTable({ cols = 5, rows = 5 }) {
  return (
    <div className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] overflow-hidden">
      <div className="px-4 py-3 bg-[#F7F8FA]">
        <Skeleton className="h-3 w-24" rounded="4px" />
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3 border-t border-[#EEF1F5]">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className="h-4 flex-1" rounded="6px" />
          ))}
        </div>
      ))}
    </div>
  );
}

function SkCardGrid({ count = 6, cols = "sm:grid-cols-2 lg:grid-cols-3" }) {
  return (
    <div className={`grid ${cols} gap-4`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4 w-24" rounded="6px" />
            <Skeleton className="h-5 w-14" rounded="999px" />
          </div>
          <Skeleton className="h-3 w-full" rounded="4px" />
          <Skeleton className="h-3 w-3/4" rounded="4px" />
          <div className="flex gap-2 pt-3 border-t border-[#EEF1F5]">
            <Skeleton className="h-3 w-10" rounded="4px" />
            <Skeleton className="h-3 w-14" rounded="4px" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkParentCards({ count = 4 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-2xl border border-[#EEF1F5] shadow-[0_6px_18px_-12px_rgba(40,50,80,.2)] p-5 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" rounded="6px" />
              <Skeleton className="h-3 w-56" rounded="4px" />
              <Skeleton className="h-3 w-44" rounded="4px" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-16" rounded="999px" />
              <Skeleton className="h-6 w-20" rounded="999px" />
              <Skeleton className="h-6 w-16" rounded="999px" />
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

function Empty({ text }) {
  return <div className="text-center py-16 text-gray-400">{text}</div>;
}
